import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useTenantNav } from '../hooks/useTenantNav';
import { startOfMonth, addMonths, format, isWithinInterval } from 'date-fns';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Chip from '@mui/material/Chip';
import Snackbar from '@mui/material/Snackbar';
import LinearProgress from '@mui/material/LinearProgress';
import Menu from '@mui/material/Menu';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Pagination from '@mui/material/Pagination';
import { GridColDef } from '@mui/x-data-grid';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { canEditPage, hasPageAccess } from '../utils/rbac';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  kanbanCardsApi,
  KanbanCard,
  financialsApi,
  ProjectFinancialEntry,
  ppmProjectsApi,
  PpmProjectListItem,
  discussionsApi,
  Discussion,
  resourceAssignmentsApi,
  ResourceAssignment,
  capacityPlansApi,
  CapacityPlan,
  requestsApi,
  RequestListItem,
  ProjectMember,
  templatesApi,
  ProjectTemplate,
} from '../api/ppm';
import { usersApi, DbUser } from '../api/client';
import { titleCase } from '../utils/format';
import './ProjectList.css';
import { ResizableDataGrid } from './grid/ResizableDataGrid';
import { tokens } from '../theme/tokens';
import { NexusBarChart } from './charts/NexusCharts';
import { computeFinance, formatCurrency, formatCurrencyShort, formatSignedCurrencyShort } from '../utils/finance';
import { StatCard } from './layout/StatCard';

type ViewMode = 'summary' | 'board' | 'list' | 'timeline' | 'finances';

// Canonical columns align with the backend enum values (status + columns stay in sync).
const defaultColumns = ['BACKLOG', 'TODO', 'IN_PROGRESS', 'IN_REVIEW', 'DONE', 'ARCHIVED'];

const canonicalColumnKey = (k: string) => {
  const raw = String(k || '').trim();
  if (!raw) return '';
  const u = raw
    .toUpperCase()
    .replace(/\s+/g, '_')
    .replace(/-+/g, '_');
  if (u === 'INPROGRESS') return 'IN_PROGRESS';
  if (u === 'TO_DO') return 'TODO';
  if (u === 'REVIEW') return 'IN_REVIEW';
  return u;
};

const columnLabel = (k: string) => {
  const c = canonicalColumnKey(k);
  if (c === 'BACKLOG') return 'Backlog';
  if (c === 'TODO') return 'To do';
  if (c === 'IN_PROGRESS') return 'In progress';
  if (c === 'IN_REVIEW') return 'In review';
  if (c === 'DONE') return 'Done';
  if (c === 'ARCHIVED') return 'Archived';
  return k;
};

const toPeriod = (d: Date) => {
  const y = d.getUTCFullYear();
  const m = `${d.getUTCMonth() + 1}`.padStart(2, '0');
  return `${y}-${m}`;
};

const InternalProjectDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { navigate } = useTenantNav();
  const location = useLocation();
  const { dbUser } = useCurrentUser();
  const canEdit = canEditPage(dbUser, 'projects');
  const canViewFinancials = hasPageAccess(dbUser, 'financials');

  const [meUser, setMeUser] = useState<DbUser | null>(null);

  const [project, setProject] = useState<PpmProjectListItem | null>(null);
  const [cards, setCards] = useState<KanbanCard[]>([]);
  const [board, setBoard] = useState<Record<string, KanbanCard[]>>({});
  const [boardId, setBoardId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('summary');

  const [financialEntries, setFinancialEntries] = useState<ProjectFinancialEntry[]>([]);
  const [financialEntriesLoading, setFinancialEntriesLoading] = useState(false);
  const [financialEntriesError, setFinancialEntriesError] = useState<string | null>(null);

  const [discussions, setDiscussions] = useState<Discussion[]>([]);
  const [members, setMembers] = useState<ProjectMember[]>([]);
  const [assignments, setAssignments] = useState<ResourceAssignment[]>([]);
  const [capacityByUserId, setCapacityByUserId] = useState<Record<string, CapacityPlan | null>>({});
  const [staffingRequests, setStaffingRequests] = useState<RequestListItem[]>([]);
  const [staffingForm, setStaffingForm] = useState<{ title: string; description: string; priority: string }>({
    title: '',
    description: '',
    priority: 'MEDIUM',
  });
  const [staffingSaving, setStaffingSaving] = useState(false);
  const [milestones, setMilestones] = useState<any[]>([]);
  const [milestoneForm, setMilestoneForm] = useState<{ name: string; description: string; status: string; plannedDate: string; percentComplete: string }>({
    name: '',
    description: '',
    status: 'NOT_STARTED',
    plannedDate: new Date().toISOString().slice(0, 10),
    percentComplete: '0',
  });
  const [milestoneSaving, setMilestoneSaving] = useState(false);
  const [editingMilestone, setEditingMilestone] = useState<any | null>(null);
  const [templateCfg, setTemplateCfg] = useState<ProjectTemplate | null>(null);

  const [artifactsAnchor, setArtifactsAnchor] = useState<null | HTMLElement>(null);
  const artifactsOpen = Boolean(artifactsAnchor);
  const [projectActionsAnchor, setProjectActionsAnchor] = useState<null | HTMLElement>(null);
  const projectActionsOpen = Boolean(projectActionsAnchor);
  const [artifactDialog, setArtifactDialog] = useState<null | 'notes' | 'files' | 'team' | 'resources' | 'staffing' | 'milestones'>(null);
  const [selectedNoteId, setSelectedNoteId] = useState<string | null>(null);

  const [allUsers, setAllUsers] = useState<DbUser[]>([]);
  const [memberForm, setMemberForm] = useState<{ userId: string; role: string }>({ userId: '', role: 'member' });
  const [membersSaving, setMembersSaving] = useState(false);

  const [noteForm, setNoteForm] = useState<{ title: string; content: string; pinned: boolean }>({
    title: '',
    content: '',
    pinned: false,
  });
  const [noteSaving, setNoteSaving] = useState(false);
  const [addNoteDialogOpen, setAddNoteDialogOpen] = useState(false);
  const [deleteNoteConfirmOpen, setDeleteNoteConfirmOpen] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState<string | null>(null);
  const [notesPage, setNotesPage] = useState(1);
  const notesPerPage = 6;

  const [fileForm, setFileForm] = useState<{ name: string; type: string; url: string }>({
    name: '',
    type: 'document',
    url: '',
  });
  const [fileSaving, setFileSaving] = useState(false);

  const [editProjectOpen, setEditProjectOpen] = useState(false);
  const [projectForm, setProjectForm] = useState<{
    name: string;
    description: string;
    status: string;
    priority: string;
    budgetedCost: string;
    leadUserId: string;
    managerUserId: string;
    startDate: string;
    plannedEndDate: string;
  }>({
    name: '',
    description: '',
    status: 'PLANNING',
    priority: 'MEDIUM',
    budgetedCost: '',
    leadUserId: '',
    managerUserId: '',
    startDate: '',
    plannedEndDate: '',
  });
  const [projectSaving, setProjectSaving] = useState(false);
  const [projectSectionsForm, setProjectSectionsForm] = useState<{
    notes: boolean;
    files: boolean;
    team: boolean;
    resources: boolean;
    staffing: boolean;
    milestones: boolean;
  }>({
    notes: false,
    files: false,
    team: false,
    resources: false,
    staffing: false,
    milestones: false,
  });

  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selected, setSelected] = useState<KanbanCard | null>(null);

  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<KanbanCard | null>(null);
  const [form, setForm] = useState({
    title: '',
    description: '',
    status: 'TODO',
    columnId: 'BACKLOG',
    dueDate: '',
    tags: '',
  });

  const [toastOpen, setToastOpen] = useState(false);
  const [toast, setToast] = useState<{ severity: 'success' | 'error'; message: string }>({ severity: 'success', message: '' });

  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dragOverCol, setDragOverCol] = useState<string | null>(null);

  const refresh = async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);

      // Always resolve the current DB user (needed for "claim/unclaim" ownership UI).
      try {
        const meRes = await usersApi.me();
        setMeUser(meRes.data || null);
      } catch {
        setMeUser(null);
      }

      // Project members (including regular users) can see project artifacts
      // Regular users can read members list to check membership, but only admins can manage
      const [projRes, boardRes, listRes, notesRes, membersRes, assignmentsRes, staffingRes] = await Promise.all([
        ppmProjectsApi.getOne(id),
        kanbanCardsApi.getBoard(id),
        kanbanCardsApi.getAll({ projectId: id }),
        discussionsApi.getAll({ projectId: id }),
        ppmProjectsApi.getMembers(id).catch(() => ({ data: [] } as any)), // All users can read members to check membership
        resourceAssignmentsApi.getAll({ projectId: id }).catch(() => ({ data: [] } as any)), // Project members can see resource assignments
        requestsApi.getAll({ projectId: id, requestType: 'Staffing' }), // Project members can see staffing requests
      ]);
      setProject(projRes.data);

      // Normalize board keys and card columns/statuses (supports legacy seeds that used "Backlog"/"In Progress"/etc.)
      const rawBoard = (boardRes.data || {}) as Record<string, KanbanCard[]>;
      const normalizedBoard: Record<string, KanbanCard[]> = {};
      Object.keys(rawBoard).forEach((k) => {
        const ck = canonicalColumnKey(k);
        if (!ck) return;
        const items = (rawBoard[k] || []).map((c: any) => ({
          ...c,
          columnId: canonicalColumnKey(c.columnId || ck),
          status: canonicalColumnKey(String(c.status || ck)),
        }));
        normalizedBoard[ck] = [...(normalizedBoard[ck] || []), ...items];
      });
      setBoard(normalizedBoard);

      setCards((listRes.data || []).map((c: any) => ({
        ...c,
        columnId: canonicalColumnKey(c.columnId || c.status || 'BACKLOG'),
        status: canonicalColumnKey(String(c.status || 'TODO')),
      })));
      const inferredBoardId = (listRes.data || [])[0]?.boardId || `demo_board_${id.replace('demo_project_', '')}`;
      setBoardId(inferredBoardId);

      setDiscussions(notesRes.data || []);
      setMembers(membersRes.data || []);
      setAssignments(assignmentsRes.data || []);
      setStaffingRequests(staffingRes.data || []);
      // Milestones come from the project's milestones relation (lowercase)
      const projectMilestones = (projRes.data as any)?.milestones || [];
      setMilestones(projectMilestones);
      // Also fetch milestones separately to ensure we have the latest (project members can see milestones)
      if (id) {
        try {
          const milestonesRes = await ppmProjectsApi.getMilestones(id);
          setMilestones(milestonesRes.data || []);
        } catch {
          // ignore; use milestones from project relation
        }
      }

      // Templates are admin-only; keep template config disabled for non-admins.
      if (canEdit) {
        const createdFromTemplateId = (projRes.data as any)?.customFields?.createdFromTemplateId as string | undefined;
        if (createdFromTemplateId) {
          try {
            const t = await templatesApi.getOne(createdFromTemplateId);
            setTemplateCfg(t.data || null);
          } catch {
            setTemplateCfg(null);
          }
        } else {
          setTemplateCfg(null);
        }
      } else {
        setTemplateCfg(null);
      }

      if (canEdit) {
        const period = toPeriod(new Date());
        const userIds = Array.from(new Set((assignmentsRes.data || []).map((a: any) => String(a.userId)).filter(Boolean))) as string[];
        const capPairs = await Promise.all(
          userIds.map(async (userId) => {
            try {
              const r = await capacityPlansApi.getAll({ userId, period });
              return [userId, (r.data && r.data.length ? r.data[0] : null)] as const;
            } catch {
              return [userId, null] as const;
            }
          }),
        );
        const nextCap: Record<string, CapacityPlan | null> = {};
        capPairs.forEach(([uid, cp]) => {
          nextCap[uid] = cp;
        });
        setCapacityByUserId(nextCap);
      } else {
        setCapacityByUserId({});
      }
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load project');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!id) return;
    if (!canViewFinancials) return; // financials endpoints require financials access
    if (viewMode !== 'finances') return;
    let mounted = true;
    (async () => {
      try {
        setFinancialEntriesLoading(true);
        setFinancialEntriesError(null);
        const res = await financialsApi.getAll({ projectId: id });
        if (!mounted) return;
        setFinancialEntries(res.data || []);
      } catch (e: any) {
        if (!mounted) return;
        setFinancialEntries([]);
        setFinancialEntriesError(e?.response?.data?.message || e?.message || 'Failed to load project financials');
      } finally {
        if (mounted) setFinancialEntriesLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [id, canViewFinancials, viewMode]);

  const boardColumns = useMemo(() => {
    const keys = Object.keys(board || {});
    // Always show the full default set (Backlog/In Progress/Review/Done),
    // then append any custom columns that exist in the data.
    const ordered: string[] = [...defaultColumns];
    keys.forEach((k) => {
      const exists = ordered.some((x) => canonicalColumnKey(x) === canonicalColumnKey(k));
      if (!exists) ordered.push(k);
    });
    return ordered;
  }, [board]);

  const stats = useMemo(() => {
    const total = cards.length;
    const open = cards.filter((c) => !String(c.status || '').toUpperCase().includes('DONE') && !String(c.status || '').toUpperCase().includes('ARCH')).length;
    const dueSoon = cards.filter((c) => {
      if (!c.dueDate) return false;
      const d = new Date(c.dueDate);
      if (Number.isNaN(d.getTime())) return false;
      const in7 = new Date();
      in7.setDate(in7.getDate() + 7);
      return d <= in7;
    }).length;
    return { total, open, dueSoon };
  }, [cards]);

  const budgetedAllocation = useMemo(() => Number((project as any)?.budgetedCost || 0), [project]);
  const actualCost = useMemo(() => Number((project as any)?.actualCost || 0), [project]);
  const finance = useMemo(
    () => computeFinance({ budget: budgetedAllocation, spent: actualCost }),
    [budgetedAllocation, actualCost],
  );

  const financialCols: GridColDef[] = useMemo(
    () => [
      {
        field: 'transactionDate',
        headerName: 'Date',
        width: 120,
        valueGetter: (params: any) => (params.row?.transactionDate ? String(params.row.transactionDate).slice(0, 10) : '—'),
      },
      {
        field: 'type',
        headerName: 'Type',
        width: 140,
        valueGetter: (params: any) => (params.row?.type ? titleCase(String(params.row.type)) : '—'),
      },
      {
        field: 'amount',
        headerName: 'Amount',
        width: 140,
        valueGetter: (params: any) => Number(params.row?.amount || 0),
        renderCell: (params: any) => <span style={{ fontWeight: 700 }}>{formatCurrency(Number(params.value || 0))}</span>,
      },
      {
        field: 'fiscal',
        headerName: 'Fiscal',
        width: 120,
        valueGetter: (params: any) => {
          const y = params.row?.fiscalYear != null ? `FY${params.row.fiscalYear}` : '';
          const q = params.row?.fiscalQuarter != null ? `Q${params.row.fiscalQuarter}` : '';
          return y && q ? `${y}-${q}` : y || q || '—';
        },
      },
      {
        field: 'category',
        headerName: 'Category',
        width: 160,
        valueGetter: (params: any) => params.row?.category || '—',
      },
      {
        field: 'description',
        headerName: 'Description',
        flex: 1,
        minWidth: 280,
        valueGetter: (params: any) => params.row?.description || '—',
      },
    ],
    [],
  );

  const filteredBoard = board;

  const applyCardUpdate = (nextCard: KanbanCard) => {
    if (!nextCard?.id) return;

    setCards((prev) => prev.map((c) => (c.id === nextCard.id ? ({ ...c, ...nextCard } as any) : c)));

    setBoard((prev) => {
      const next: Record<string, KanbanCard[]> = {};
      Object.keys(prev || {}).forEach((k) => {
        next[k] = (prev[k] || []).map((c) => (c.id === nextCard.id ? ({ ...c, ...nextCard } as any) : c));
      });
      return next;
    });

    setSelected((prev) => (prev && prev.id === nextCard.id ? ({ ...prev, ...nextCard } as any) : prev));
  };

  const openCard = (c: KanbanCard) => {
    setSelected(c);
    setDetailsOpen(true);
  };

  const claimCard = async (c: KanbanCard) => {
    try {
      const r = await kanbanCardsApi.claim(c.id);
      applyCardUpdate(r.data as any);
      setToast({ severity: 'success', message: 'Task claimed' });
      setToastOpen(true);
    } catch (e: any) {
      setToast({ severity: 'error', message: e?.response?.data?.message || e?.message || 'Claim failed' });
      setToastOpen(true);
    }
  };

  const unclaimCard = async (c: KanbanCard) => {
    try {
      const r = await kanbanCardsApi.unclaim(c.id);
      applyCardUpdate(r.data as any);
      setToast({ severity: 'success', message: 'Task unclaimed' });
      setToastOpen(true);
    } catch (e: any) {
      setToast({ severity: 'error', message: e?.response?.data?.message || e?.message || 'Unclaim failed' });
      setToastOpen(true);
    }
  };

  const moveCard = async (cardId: string, targetColumn: string) => {
    if (!cardId) return;
    if (!id) return;

    const card = cards.find((c) => c.id === cardId);
    if (!card) return;

    const currentColumn = canonicalColumnKey(card.columnId || (card as any).status || '');
    const targetKey = canonicalColumnKey(targetColumn);
    const targetPos = (board[targetKey] || []).filter((c) => c.id !== cardId).length;

    // optimistic UI
    setBoard((prev) => {
      const next: Record<string, KanbanCard[]> = {};
      Object.keys(prev).forEach((k) => {
        next[k] = (prev[k] || []).filter((c) => c.id !== cardId);
      });
      next[targetKey] = [...(next[targetKey] || []), { ...card, columnId: targetKey, status: targetKey as any }];
      return next;
    });
    setCards((prev) => prev.map((c) => (c.id === cardId ? { ...c, columnId: targetKey, status: targetKey as any } : c)));

    if (currentColumn === targetKey) return;

    try {
      await kanbanCardsApi.move(cardId, targetKey, targetPos);
    } catch (e: any) {
      setToast({ severity: 'error', message: e?.response?.data?.message || e?.message || 'Move failed' });
      setToastOpen(true);
      refresh();
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm({
      title: '',
      description: '',
      status: defaultColumns[0],
      columnId: defaultColumns[0],
      dueDate: '',
      tags: '',
    });
    setEditOpen(true);
  };

  const openCreateInColumn = (col: string) => {
    openCreate();
    const ck = canonicalColumnKey(col);
    setForm((f) => ({ ...f, columnId: ck, status: ck }));
  };

  const openEdit = (c: KanbanCard) => {
    setEditing(c);
    const ck = canonicalColumnKey(c.columnId || (c as any).status || defaultColumns[0]);
    setForm({
      title: c.title || '',
      description: c.description || '',
      status: String(c.status || ck || 'TODO'),
      columnId: ck || defaultColumns[0],
      dueDate: c.dueDate ? String(c.dueDate).slice(0, 10) : '',
      tags: (c.tags || []).join(', '),
    });
    setEditOpen(true);
  };

  const save = async () => {
    if (!id) return;
    if (!form.title.trim()) {
      setToast({ severity: 'error', message: 'Title is required' });
      setToastOpen(true);
      return;
    }
    try {
      const tags = form.tags
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean);

      const payload: any = {
        title: form.title.trim(),
        description: form.description.trim() ? form.description.trim() : undefined,
        status: canonicalColumnKey(form.columnId || form.status) || 'TODO',
        columnId: canonicalColumnKey(form.columnId || form.status) || 'TODO',
        dueDate: form.dueDate ? new Date(`${form.dueDate}T00:00:00.000Z`) : undefined,
        tags,
      };

      if (editing) {
        await kanbanCardsApi.update(editing.id, payload);
        setToast({ severity: 'success', message: 'Task updated' });
      } else {
        // NOTE: requires a board to already exist for this project in DB seed.
        await kanbanCardsApi.create({
          ...payload,
          Board: { connect: { id: boardId || `demo_board_${id.replace('demo_project_', '')}` } },
        });
        setToast({ severity: 'success', message: 'Task created' });
      }

      setToastOpen(true);
      setEditOpen(false);
      refresh();
    } catch (e: any) {
      setToast({ severity: 'error', message: e?.response?.data?.message || e?.message || 'Save failed' });
      setToastOpen(true);
    }
  };

  const remove = async (c: KanbanCard) => {
    // eslint-disable-next-line no-alert
    const ok = window.confirm(`Delete "${c.title}"?`);
    if (!ok) return;
    try {
      await kanbanCardsApi.remove(c.id);
      setToast({ severity: 'success', message: 'Deleted' });
      setToastOpen(true);
      setDetailsOpen(false);
      refresh();
    } catch (e: any) {
      setToast({ severity: 'error', message: e?.response?.data?.message || e?.message || 'Delete failed' });
      setToastOpen(true);
    }
  };

  const cols: GridColDef[] = [
    { field: 'title', headerName: 'Summary', flex: 1, minWidth: 260 },
    { field: 'status', headerName: 'Status', width: 140, valueGetter: (p) => titleCase(String(p.row?.status || '—')) },
    { field: 'columnId', headerName: 'Column', width: 160, valueGetter: (p) => p.row?.columnId || '—' },
    {
      field: 'assigneeId',
      headerName: 'Assignee',
      width: 200,
      valueGetter: (p) => {
        const row: any = p.row || {};
        const a = row.Assignee;
        if (a?.displayName) return a.displayName;
        if (a?.email) return a.email;
        if (row.assigneeId && meUser?.id && row.assigneeId === meUser.id) return 'Me';
        return '—';
      },
    },
    { field: 'dueDate', headerName: 'Due', width: 140, valueGetter: (p) => (p.row?.dueDate ? String(p.row.dueDate).slice(0, 10) : '—') },
  ];

  const filePlaceholders = useMemo(() => {
    const cf: any = project?.customFields || {};
    return Array.isArray(cf.filePlaceholders) ? cf.filePlaceholders : [];
  }, [project]);

  const createdFromTemplateId = useMemo(() => {
    return (project as any)?.customFields?.createdFromTemplateId ? String((project as any).customFields.createdFromTemplateId) : null;
  }, [project]);

  const templateSections = useMemo(() => {
    const cf: any = project?.customFields || {};
    const ts: any = cf.templateSections || {};
    return {
      notes: !!ts.notes || !!templateCfg?.includeNotes,
      files: !!ts.files || !!templateCfg?.includeFiles,
      team: !!ts.team || !!templateCfg?.includePermissions,
      resources: !!ts.resources || !!templateCfg?.includeResources,
      resourcePlanning: !!ts.resourcePlanning || !!templateCfg?.includeResourcePlanning,
      staffing: !!ts.staffing || !!templateCfg?.includeStaffingRequests,
      milestones: !!ts.milestones || !!templateCfg?.includeMilestones,
    };
  }, [project, templateCfg]);

  const uiSections = useMemo(() => {
    const cf: any = project?.customFields || {};
    const ui: any = cf.uiSections || {};
    return {
      notes: !!ui.notes,
      files: !!ui.files,
      team: !!ui.team,
      resources: !!ui.resources,
      staffing: !!ui.staffing,
      milestones: !!ui.milestones,
    };
  }, [project]);

  const isProjectMember = useMemo(() => {
    if (!meUser?.id) return false;
    if (canEdit) return true; // Admins are always considered members
    // If user can access this project page, they're a member (backend already validated access)
    // Also check explicit membership, lead, manager, or resource assignment
    return (
      members.some((m) => m.userId === meUser.id) ||
      project?.leadUserId === meUser.id ||
      project?.managerUserId === meUser.id ||
      assignments.some((a) => a.userId === meUser.id) ||
      !!project // If project exists and user can see it, they're a member
    );
  }, [meUser?.id, members, assignments, project, canEdit]);

  const showNotesBlock = (canEdit || isProjectMember) && (discussions.length > 0 || templateSections.notes || uiSections.notes);
  const showFilesBlock = (canEdit || isProjectMember) && (filePlaceholders.length > 0 || templateSections.files || uiSections.files);
  const showTeamBlock = (canEdit || isProjectMember) && (members.length > 0 || templateSections.team || uiSections.team);
  const showResourcesBlock = (canEdit || isProjectMember) && (assignments.length > 0 || templateSections.resources || templateSections.resourcePlanning || uiSections.resources);
  const showStaffingBlock = (canEdit || isProjectMember) && (staffingRequests.length > 0 || templateSections.staffing || uiSections.staffing);
  const showMilestonesBlock = (canEdit || isProjectMember) && (milestones.length > 0 || templateSections.milestones || uiSections.milestones);

  const sectionCardSx = (accent: string) => ({
    cursor: 'pointer',
    border: `1px solid ${tokens.color.border}`,
    position: 'relative' as const,
    overflow: 'hidden',
    backgroundColor: tokens.color.surface,
    '&::before': {
      content: '""',
      position: 'absolute' as const,
      left: 0,
      top: 0,
      height: 3,
      width: '100%',
      background: accent,
    },
    transition: 'border-color 120ms ease, background-color 120ms ease',
    '&:hover': {
      borderColor: tokens.color.borderStrong,
      backgroundColor: tokens.color.surfaceMuted,
    },
  });

  const openArtifact = async (which: NonNullable<typeof artifactDialog>) => {
    if (!canEdit && !isProjectMember) return;
    setArtifactDialog(which);
    if ((which === 'team' || which === 'notes' || which === 'staffing' || which === 'resources' || which === 'files') && allUsers.length === 0 && canEdit) {
      try {
        const r = await usersApi.getAll({ scopePage: 'projects' });
        setAllUsers(r.data || []);
      } catch {
        // ignore; team dialog can still render current members
      }
    }
  };

  const addMember = async () => {
    if (!id) return;
    if (!memberForm.userId) return;
    try {
      setMembersSaving(true);
      const r = await ppmProjectsApi.addMember(id, { userId: memberForm.userId, role: memberForm.role });
      setMembers(r.data || []);
      setMemberForm({ userId: '', role: 'member' });
    } catch (e: any) {
      setToast({ severity: 'error', message: e?.response?.data?.message || e?.message || 'Failed to add member' });
      setToastOpen(true);
    } finally {
      setMembersSaving(false);
    }
  };

  const updateMemberRole = async (memberId: string, role: string) => {
    if (!id) return;
    try {
      const r = await ppmProjectsApi.updateMember(id, memberId, { role });
      setMembers(r.data || []);
    } catch (e: any) {
      setToast({ severity: 'error', message: e?.response?.data?.message || e?.message || 'Failed to update role' });
      setToastOpen(true);
    }
  };

  const removeMember = async (memberId: string) => {
    if (!id) return;
    try {
      const r = await ppmProjectsApi.removeMember(id, memberId);
      setMembers(r.data || []);
    } catch (e: any) {
      setToast({ severity: 'error', message: e?.response?.data?.message || e?.message || 'Failed to remove member' });
      setToastOpen(true);
    }
  };

  const createNote = async () => {
    if (!id || !meUser?.id) {
      setToast({ severity: 'error', message: 'Unable to create note. Please try again.' });
      setToastOpen(true);
      return;
    }
    const contentHtml = noteForm.content.trim();
    const contentText = contentHtml.replace(/<[^>]*>/g, '').trim();
    if (!noteForm.title.trim() || !contentText) {
      setToast({ severity: 'error', message: 'Title and content are required.' });
      setToastOpen(true);
      return;
    }

    try {
      setNoteSaving(true);
      const payload: any = {
        title: noteForm.title.trim(),
        projectId: id,
        creatorId: meUser.id,
        isPinned: !!noteForm.pinned,
        Posts: {
          create: [
            {
              userId: meUser.id,
              content: contentHtml,
            },
          ],
        },
      };
      await discussionsApi.create(payload);
      setToast({ severity: 'success', message: 'Note added' });
      setToastOpen(true);
      setNoteForm({ title: '', content: '', pinned: false });
      setAddNoteDialogOpen(false);
      refresh();
    } catch (e: any) {
      setToast({ severity: 'error', message: e?.response?.data?.message || e?.message || 'Failed to add note' });
      setToastOpen(true);
    } finally {
      setNoteSaving(false);
    }
  };

  const handleDeleteNote = (noteId: string) => {
    setNoteToDelete(noteId);
    setDeleteNoteConfirmOpen(true);
  };

  const confirmDeleteNote = async () => {
    if (!noteToDelete) return;
    try {
      await discussionsApi.remove(noteToDelete);
      setToast({ severity: 'success', message: 'Note deleted' });
      setToastOpen(true);
      if (selectedNoteId === noteToDelete) {
        setSelectedNoteId(null);
      }
      setDeleteNoteConfirmOpen(false);
      setNoteToDelete(null);
      refresh();
    } catch (e: any) {
      setToast({ severity: 'error', message: e?.response?.data?.message || e?.message || 'Failed to delete note' });
      setToastOpen(true);
    }
  };

  const addFilePlaceholder = async () => {
    if (!id) return;
    if (!fileForm.name.trim()) {
      setToast({ severity: 'error', message: 'File name is required.' });
      setToastOpen(true);
      return;
    }
    try {
      setFileSaving(true);
      const current: any = project?.customFields || {};
      const existing = Array.isArray(current.filePlaceholders) ? current.filePlaceholders : [];
      const next = [
        ...existing,
        {
          name: fileForm.name.trim(),
          type: fileForm.type.trim() ? fileForm.type.trim() : undefined,
          url: fileForm.url.trim() ? fileForm.url.trim() : undefined,
          addedAt: new Date().toISOString(),
        },
      ];

      const r = await ppmProjectsApi.update(id, { customFields: { ...current, filePlaceholders: next } });
      setProject(r.data);
      setToast({ severity: 'success', message: 'File placeholder added' });
      setToastOpen(true);
      setFileForm({ name: '', type: 'document', url: '' });
      refresh();
    } catch (e: any) {
      setToast({ severity: 'error', message: e?.response?.data?.message || e?.message || 'Failed to add file placeholder' });
      setToastOpen(true);
    } finally {
      setFileSaving(false);
    }
  };

  const deleteFilePlaceholder = async (idx: number) => {
    if (!id) return;
    try {
      const current: any = project?.customFields || {};
      const existing = Array.isArray(current.filePlaceholders) ? current.filePlaceholders : [];
      const next = existing.filter((_: any, i: number) => i !== idx);
      const r = await ppmProjectsApi.update(id, { customFields: { ...current, filePlaceholders: next } });
      setProject(r.data);
      setToast({ severity: 'success', message: 'File placeholder deleted' });
      setToastOpen(true);
      refresh();
    } catch (e: any) {
      setToast({ severity: 'error', message: e?.response?.data?.message || e?.message || 'Failed to delete file placeholder' });
      setToastOpen(true);
    }
  };

  const deleteStaffingRequest = async (requestId: string) => {
    try {
      await requestsApi.remove(requestId);
      setToast({ severity: 'success', message: 'Staffing request deleted' });
      setToastOpen(true);
      refresh();
    } catch (e: any) {
      setToast({ severity: 'error', message: e?.response?.data?.message || e?.message || 'Failed to delete request' });
      setToastOpen(true);
    }
  };

  const addStaffingRequest = async () => {
    if (!canEdit) return;
    if (!id) return;
    if (!staffingForm.title.trim()) {
      setToast({ severity: 'error', message: 'Title is required' });
      setToastOpen(true);
      return;
    }
    try {
      setStaffingSaving(true);
      await requestsApi.create({
        title: staffingForm.title.trim(),
        description: staffingForm.description.trim() ? staffingForm.description.trim() : undefined,
        requestType: 'Staffing',
        status: 'SUBMITTED',
        priority: staffingForm.priority || 'MEDIUM',
        projectId: id,
      });
      setToast({ severity: 'success', message: 'Staffing request created' });
      setToastOpen(true);
      setStaffingForm({ title: '', description: '', priority: 'MEDIUM' });
      const staffingRes = await requestsApi.getAll({ projectId: id, requestType: 'Staffing' });
      setStaffingRequests(staffingRes.data || []);
    } catch (e: any) {
      setToast({ severity: 'error', message: e?.response?.data?.message || e?.message || 'Failed to create staffing request' });
      setToastOpen(true);
    } finally {
      setStaffingSaving(false);
    }
  };

  const addMilestone = async () => {
    if (!canEdit) return;
    if (!id) return;
    if (!milestoneForm.name.trim()) {
      setToast({ severity: 'error', message: 'Milestone name is required' });
      setToastOpen(true);
      return;
    }
    try {
      setMilestoneSaving(true);
      await ppmProjectsApi.createMilestone(id, {
        name: milestoneForm.name.trim(),
        description: milestoneForm.description.trim() ? milestoneForm.description.trim() : undefined,
        status: milestoneForm.status,
        plannedDate: milestoneForm.plannedDate,
        percentComplete: Number(milestoneForm.percentComplete) || 0,
      });
      setToast({ severity: 'success', message: 'Milestone created' });
      setToastOpen(true);
      setMilestoneForm({ name: '', description: '', status: 'NOT_STARTED', plannedDate: new Date().toISOString().slice(0, 10), percentComplete: '0' });
      const milestonesRes = await ppmProjectsApi.getMilestones(id);
      setMilestones(milestonesRes.data || []);
    } catch (e: any) {
      setToast({ severity: 'error', message: e?.response?.data?.message || e?.message || 'Failed to create milestone' });
      setToastOpen(true);
    } finally {
      setMilestoneSaving(false);
    }
  };

  const updateMilestone = async (milestoneId: string) => {
    if (!canEdit) return;
    if (!id) return;
    if (!milestoneForm.name.trim()) {
      setToast({ severity: 'error', message: 'Milestone name is required' });
      setToastOpen(true);
      return;
    }
    try {
      setMilestoneSaving(true);
      await ppmProjectsApi.updateMilestone(id, milestoneId, {
        name: milestoneForm.name.trim(),
        description: milestoneForm.description.trim() ? milestoneForm.description.trim() : undefined,
        status: milestoneForm.status,
        plannedDate: milestoneForm.plannedDate,
        percentComplete: Number(milestoneForm.percentComplete) || 0,
      });
      setToast({ severity: 'success', message: 'Milestone updated' });
      setToastOpen(true);
      setEditingMilestone(null);
      setMilestoneForm({ name: '', description: '', status: 'NOT_STARTED', plannedDate: new Date().toISOString().slice(0, 10), percentComplete: '0' });
      const milestonesRes = await ppmProjectsApi.getMilestones(id);
      setMilestones(milestonesRes.data || []);
    } catch (e: any) {
      setToast({ severity: 'error', message: e?.response?.data?.message || e?.message || 'Failed to update milestone' });
      setToastOpen(true);
    } finally {
      setMilestoneSaving(false);
    }
  };

  const deleteMilestone = async (milestoneId: string) => {
    if (!canEdit) return;
    if (!id) return;
    try {
      await ppmProjectsApi.removeMilestone(id, milestoneId);
      setToast({ severity: 'success', message: 'Milestone deleted' });
      setToastOpen(true);
      const milestonesRes = await ppmProjectsApi.getMilestones(id);
      setMilestones(milestonesRes.data || []);
    } catch (e: any) {
      setToast({ severity: 'error', message: e?.response?.data?.message || e?.message || 'Failed to delete milestone' });
      setToastOpen(true);
    }
  };

  const [resourceForm, setResourceForm] = useState<{ userId: string; role: string; hoursAllocated: string; startDate: string; status: string; notes: string }>({
    userId: '',
    role: 'Engineer',
    hoursAllocated: '40',
    startDate: new Date().toISOString().slice(0, 10),
    status: 'ASSIGNED',
    notes: 'Added from project',
  });
  const [resourceSaving, setResourceSaving] = useState(false);

  const addResource = async () => {
    if (!id) return;
    if (!resourceForm.userId) {
      setToast({ severity: 'error', message: 'Select a user' });
      setToastOpen(true);
      return;
    }
    try {
      setResourceSaving(true);
      const payload: any = {
        role: resourceForm.role.trim() ? resourceForm.role.trim() : undefined,
        hoursAllocated: resourceForm.hoursAllocated.trim() ? Number(resourceForm.hoursAllocated) : undefined,
        startDate: resourceForm.startDate ? new Date(`${resourceForm.startDate}T00:00:00.000Z`) : new Date(),
        status: resourceForm.status,
        notes: resourceForm.notes.trim() ? resourceForm.notes.trim() : undefined,
        User: { connect: { id: resourceForm.userId } },
        Project: { connect: { id } },
      };
      await resourceAssignmentsApi.create(payload);
      setToast({ severity: 'success', message: 'Resource added' });
      setToastOpen(true);
      setResourceForm((f) => ({ ...f, userId: '', hoursAllocated: '40', role: 'Engineer' }));
      refresh();
    } catch (e: any) {
      setToast({ severity: 'error', message: e?.response?.data?.message || e?.message || 'Failed to add resource' });
      setToastOpen(true);
    } finally {
      setResourceSaving(false);
    }
  };

  const removeResource = async (assignmentId: string) => {
    try {
      await resourceAssignmentsApi.remove(assignmentId);
      setToast({ severity: 'success', message: 'Resource removed' });
      setToastOpen(true);
      refresh();
    } catch (e: any) {
      setToast({ severity: 'error', message: e?.response?.data?.message || e?.message || 'Failed to remove resource' });
      setToastOpen(true);
    }
  };

  const openEditProject = () => {
    const cf: any = project?.customFields || {};
    const ui: any = cf.uiSections || {};
    const ts: any = cf.templateSections || {};
    const budgeted = project?.budgetedCost != null ? String(project.budgetedCost) : '';
    setProjectForm({
      name: project?.name || '',
      description: project?.description || '',
      status: String(project?.status || 'PLANNING'),
      priority: String(project?.priority || 'MEDIUM'),
      budgetedCost: budgeted,
      leadUserId: String((project as any)?.leadUserId || (project as any)?.Lead?.id || ''),
      managerUserId: String((project as any)?.managerUserId || (project as any)?.Manager?.id || ''),
      startDate: (project as any)?.startDate ? String((project as any).startDate).slice(0, 10) : '',
      plannedEndDate: (project as any)?.plannedEndDate ? String((project as any).plannedEndDate).slice(0, 10) : '',
    });
    setProjectSectionsForm({
      notes: !!ui.notes || !!ts.notes,
      files: !!ui.files || !!ts.files,
      team: !!ui.team || !!ts.team,
      resources: !!ui.resources || !!ts.resources || !!ts.resourcePlanning,
      staffing: !!ui.staffing || !!ts.staffing,
      milestones: !!ui.milestones || !!ts.milestones,
    });
    // Ensure we have users available for Lead/Manager selectors.
    if (allUsers.length === 0) {
      usersApi.getAll({ scopePage: 'projects' }).then((r) => setAllUsers(r.data || [])).catch(() => undefined);
    }
    setEditProjectOpen(true);
  };

  const saveProject = async () => {
    if (!id) return;
    if (!projectForm.name.trim()) {
      setToast({ severity: 'error', message: 'Project name is required' });
      setToastOpen(true);
      return;
    }
    try {
      setProjectSaving(true);
      const numOrNull = (v: string) => {
        const t = (v || '').trim();
        return t === '' ? null : Number(t);
      };
      const budgetedCost = numOrNull(projectForm.budgetedCost);
      const current: any = project?.customFields || {};
      const payload: any = {
        name: projectForm.name.trim(),
        description: projectForm.description.trim() ? projectForm.description.trim() : null,
        status: projectForm.status as any,
        priority: projectForm.priority as any,
        budgetedCost,
        leadUserId: projectForm.leadUserId ? projectForm.leadUserId : null,
        managerUserId: projectForm.managerUserId ? projectForm.managerUserId : null,
        startDate: projectForm.startDate ? new Date(`${projectForm.startDate}T00:00:00.000Z`) : null,
        plannedEndDate: projectForm.plannedEndDate ? new Date(`${projectForm.plannedEndDate}T00:00:00.000Z`) : null,
        customFields: {
          ...current,
          uiSections: {
            notes: !!projectSectionsForm.notes,
            files: !!projectSectionsForm.files,
            team: !!projectSectionsForm.team,
            resources: !!projectSectionsForm.resources,
            staffing: !!projectSectionsForm.staffing,
            milestones: !!projectSectionsForm.milestones,
          },
        },
      };
      const r = await ppmProjectsApi.update(id, payload);
      setProject(r.data);
      setEditProjectOpen(false);
      setToast({ severity: 'success', message: 'Project updated' });
      setToastOpen(true);
      refresh();
    } catch (e: any) {
      setToast({ severity: 'error', message: e?.response?.data?.message || e?.message || 'Failed to update project' });
      setToastOpen(true);
    } finally {
      setProjectSaving(false);
    }
  };

  const deleteProject = async () => {
    if (!id) return;
    // eslint-disable-next-line no-alert
    const ok = window.confirm(`Delete project "${project?.name || id}"? This cannot be undone.`);
    if (!ok) return;
    try {
      await ppmProjectsApi.remove(id);
      setToast({ severity: 'success', message: 'Project deleted' });
      setToastOpen(true);
      navigate('/projects');
    } catch (e: any) {
      setToast({ severity: 'error', message: e?.response?.data?.message || e?.message || 'Failed to delete project' });
      setToastOpen(true);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 240 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      {error ? (
        <Alert severity="error" variant="filled" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      <div className="project-detail-view">
        <div className="project-detail-header">
          <button
            className="back-button"
            onClick={() => {
              const from = (location as any)?.state?.from;
              if (typeof from === 'string' && from.trim()) {
                navigate(from);
                return;
              }
              // Fallback: if we know the parent portfolio, go back there (especially if coming from portfolio page).
              const pid = (project as any)?.Portfolio?.id || (project as any)?.portfolioId;
              if (pid) {
                navigate(`/portfolios/${pid}`);
                return;
              }
              // Last resort: go to projects list
              navigate('/projects');
            }}
          >
            ← Back to Projects
          </button>

          <div className="project-header-info">
            <div>
              <h2>{project?.name || 'Project'}</h2>
              <div className="project-header-meta">
                <span className="project-key-badge">
                  {project?.jiraKey ? `Jira: ${project.jiraKey}` : project?.name ? `Project: ${project.name}` : 'Project'}
                </span>
                <span className="project-lead">{project?.Portfolio?.name ? `Portfolio: ${project.Portfolio.name}` : '—'}</span>
              </div>
              {project?.description ? <p className="project-description">{project.description}</p> : null}
            </div>
          </div>
        </div>

        <div className="view-tabs">
          <button className={`view-tab ${viewMode === 'summary' ? 'active' : ''}`} onClick={() => setViewMode('summary')}>
            Summary
          </button>
          <button className={`view-tab ${viewMode === 'board' ? 'active' : ''}`} onClick={() => setViewMode('board')}>
            Board
          </button>
          <button className={`view-tab ${viewMode === 'list' ? 'active' : ''}`} onClick={() => setViewMode('list')}>
            List
          </button>
          <button className={`view-tab ${viewMode === 'timeline' ? 'active' : ''}`} onClick={() => setViewMode('timeline')}>
            Timeline
          </button>
          <button
            className={`view-tab ${viewMode === 'finances' ? 'active' : ''}`}
            onClick={() => setViewMode('finances')}
            disabled={!canViewFinancials}
            title={!canViewFinancials ? 'No access to financials' : 'Finances'}
          >
            Finances
          </button>

          <div style={{ flex: 1 }} />
          <Tooltip title="Template items (notes, files, resources...)">
            <IconButton onClick={(e) => setArtifactsAnchor(e.currentTarget)} sx={{ mr: 0.5 }}>
              <FolderOpenIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Menu anchorEl={artifactsAnchor} open={artifactsOpen} onClose={() => setArtifactsAnchor(null)}>
            {(canEdit || isProjectMember) ? (
              <>
                {showTeamBlock ? (
                  <MenuItem onClick={() => { setArtifactsAnchor(null); openArtifact('team'); }}>
                    Team & Permissions ({members.length})
                  </MenuItem>
                ) : null}
                {showNotesBlock ? (
                  <MenuItem onClick={() => { setArtifactsAnchor(null); openArtifact('notes'); }}>
                    Notes ({discussions.length})
                  </MenuItem>
                ) : null}
                {showFilesBlock ? (
                  <MenuItem onClick={() => { setArtifactsAnchor(null); openArtifact('files'); }}>
                    Files ({filePlaceholders.length})
                  </MenuItem>
                ) : null}
                {showResourcesBlock ? (
                  <MenuItem onClick={() => { setArtifactsAnchor(null); openArtifact('resources'); }}>
                    Resources ({assignments.length})
                  </MenuItem>
                ) : null}
                {showStaffingBlock ? (
                  <MenuItem onClick={() => { setArtifactsAnchor(null); openArtifact('staffing'); }}>
                    Staffing Requests ({staffingRequests.length})
                  </MenuItem>
                ) : null}
                {showMilestonesBlock ? (
                  <MenuItem onClick={() => { setArtifactsAnchor(null); openArtifact('milestones'); }}>
                    Milestones ({milestones.length})
                  </MenuItem>
                ) : null}
              </>
            ) : (
              <MenuItem disabled>
                No project artifacts available
              </MenuItem>
            )}
          </Menu>

          <Tooltip title="Project actions">
            <IconButton onClick={(e) => setProjectActionsAnchor(e.currentTarget)} sx={{ mr: 0.5 }}>
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Menu anchorEl={projectActionsAnchor} open={projectActionsOpen} onClose={() => setProjectActionsAnchor(null)}>
            <MenuItem
              disabled={!canEdit}
              onClick={() => {
                setProjectActionsAnchor(null);
                openEditProject();
              }}
            >
              <EditIcon fontSize="small" style={{ marginRight: 8 }} /> Edit project
            </MenuItem>
            <MenuItem
              disabled={!canEdit}
              onClick={() => {
                setProjectActionsAnchor(null);
                deleteProject();
              }}
            >
              <DeleteIcon fontSize="small" style={{ marginRight: 8 }} /> Delete project
            </MenuItem>
          </Menu>
          <Tooltip title="Refresh">
            <IconButton onClick={refresh} sx={{ mr: 0.5 }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={openCreate}
            sx={{ minHeight: 30, py: 0.25, px: 1.25, fontSize: 12, borderRadius: 2 }}
          >
            Add task
          </Button>
        </div>

        {viewMode === 'summary' ? (
          <div className="view-content">
            <div className="summary-stats">
              <div className="stat-box">
                <div className="stat-value">{stats.total}</div>
                <div className="stat-label">Total Tasks</div>
              </div>
              <div className="stat-box">
                <div className="stat-value">{stats.open}</div>
                <div className="stat-label">Open</div>
              </div>
              <div className="stat-box">
                <div className="stat-value">{stats.dueSoon}</div>
                <div className="stat-label">Due (7d)</div>
              </div>
            </div>

            {/* Staggered blocks (Overview-style) for the main template artifacts */}
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              {showNotesBlock ? (
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={sectionCardSx('#6366F1')} onClick={() => openArtifact('notes')}>
                  <CardContent>
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 650 }}>
                        Notes
                      </Typography>
                      <Chip size="small" variant="outlined" label={discussions.length} sx={{ height: 24, fontWeight: 650 }} />
                    </Stack>
                    {discussions.length ? (
                      <>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {discussions[0]?.title || 'Discussion'}
                        </Typography>
                        {discussions[0]?.Posts?.[0]?.content ? (
                          <Box
                            sx={{
                              mt: 0.5,
                              position: 'relative',
                              maxHeight: '3em',
                              overflow: 'hidden',
                              wordWrap: 'break-word',
                              overflowWrap: 'break-word',
                              '&::after': {
                                content: '""',
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                right: 0,
                                height: '16px',
                                background: 'linear-gradient(to bottom, rgba(255,255,255,0), rgba(255,255,255,1))',
                                pointerEvents: 'none',
                              },
                              '& .ql-editor': {
                                padding: 0,
                                fontSize: '0.875rem',
                                lineHeight: 1.4,
                                color: 'text.secondary',
                              },
                              '& .ql-container': {
                                border: 'none',
                                fontFamily: 'inherit',
                              },
                              '& ol, & ul': {
                                paddingLeft: '1.5em',
                                marginLeft: 0,
                                marginTop: '0.25em',
                                marginBottom: '0.25em',
                              },
                              '& li': {
                                marginBottom: '0.125em',
                              },
                              '& p': {
                                marginTop: '0.25em',
                                marginBottom: '0.25em',
                                '&:first-of-type': {
                                  marginTop: 0,
                                },
                                '&:last-of-type': {
                                  marginBottom: 0,
                                },
                              },
                            }}
                          >
                            <div dangerouslySetInnerHTML={{ __html: discussions[0].Posts[0].content }} />
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            Open to view and manage notes.
                          </Typography>
                        )}
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {canEdit ? 'No notes yet — click to add one.' : 'No notes yet.'}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              ) : null}

              {showFilesBlock ? (
              <Grid item xs={12} md={6}>
                <Card
                  variant="outlined"
                  sx={sectionCardSx('#0EA5E9')}
                  onClick={() => openArtifact('files')}
                >
                  <CardContent>
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Box sx={{ width: 4, height: 20, bgcolor: '#0EA5E9', borderRadius: 1 }} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 650 }}>
                          Files
                        </Typography>
                      </Stack>
                      <Chip
                        size="small"
                        label={filePlaceholders.length}
                        sx={{
                          height: 24,
                          fontWeight: 650,
                          bgcolor: filePlaceholders.length > 0 ? tokens.color.accentSoft : 'transparent',
                          color: filePlaceholders.length > 0 ? tokens.color.accent : 'inherit',
                        }}
                      />
                    </Stack>
                    {filePlaceholders.length ? (
                      <Stack spacing={1}>
                        {filePlaceholders.slice(0, 3).map((f: any, idx: number) => (
                          <Box
                            key={`${f?.name || 'file'}_${idx}`}
                            sx={{
                              p: 1,
                              bgcolor: tokens.color.surface,
                              borderRadius: 1,
                              border: `1px solid ${tokens.color.border}`,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            <Box sx={{ width: 24, height: 24, bgcolor: tokens.color.neutralSoft, borderRadius: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>📄</Typography>
                            </Box>
                            <Typography variant="body2" sx={{ flex: 1, fontWeight: 500 }} noWrap>
                              {String(f?.name || 'Untitled')}
                            </Typography>
                            {f?.type ? (
                              <Chip size="small" label={f.type} sx={{ height: 18, fontSize: '0.65rem' }} />
                            ) : null}
                          </Box>
                        ))}
                        {filePlaceholders.length > 3 ? (
                          <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', pt: 0.5 }}>
                            +{filePlaceholders.length - 3} more file{filePlaceholders.length - 3 !== 1 ? 's' : ''}
                          </Typography>
                        ) : null}
                      </Stack>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {canEdit ? 'No files yet — click to add one.' : 'No files yet.'}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              ) : null}

              {showTeamBlock ? (
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={sectionCardSx('#10B981')} onClick={() => openArtifact('team')}>
                  <CardContent>
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 650 }}>
                        Team
                      </Typography>
                      <Chip size="small" variant="outlined" label={members.length} sx={{ height: 24, fontWeight: 650 }} />
                    </Stack>
                    {members.length ? (
                      <Stack spacing={0.5}>
                        {members.slice(0, 3).map((m, idx) => (
                          <Typography key={m.id} variant="body2" color="text.secondary" noWrap>
                            - {(m.User?.displayName || m.User?.email || m.userId) + ` (${m.role || 'member'})`}
                          </Typography>
                        ))}
                        {members.length > 3 ? (
                          <Typography variant="caption" color="text.secondary">
                            +{members.length - 3} more
                          </Typography>
                        ) : null}
                      </Stack>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {canEdit ? 'No members yet — click to add someone.' : 'No team members yet.'}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              ) : null}

              {showResourcesBlock ? (
              <Grid item xs={12} md={6}>
                <Card
                  variant="outlined"
                  sx={sectionCardSx('#F59E0B')}
                  onClick={() => openArtifact('resources')}
                >
                  <CardContent>
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Box sx={{ width: 4, height: 20, bgcolor: '#F59E0B', borderRadius: 1 }} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 650 }}>
                          Resources
                        </Typography>
                      </Stack>
                      <Chip
                        size="small"
                        label={assignments.length}
                        sx={{
                          height: 24,
                          fontWeight: 650,
                          bgcolor: assignments.length > 0 ? tokens.color.warningSoft : 'transparent',
                          color: assignments.length > 0 ? tokens.color.warning : 'inherit',
                        }}
                      />
                    </Stack>
                    {assignments.length ? (
                      <Stack spacing={1}>
                        {assignments.slice(0, 2).map((a) => (
                          <Box
                            key={a.id}
                            sx={{
                              p: 1,
                              bgcolor: tokens.color.surface,
                              borderRadius: 1,
                              border: `1px solid ${tokens.color.border}`,
                            }}
                          >
                            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                              <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                                {a.User?.displayName || a.User?.email || a.userId}
                              </Typography>
                              {a.role ? (
                                <Chip size="small" label={a.role} sx={{ height: 20, fontSize: '0.7rem' }} />
                              ) : null}
                            </Stack>
                            {a.hoursAllocated ? (
                              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                {a.hoursAllocated}h allocated
                              </Typography>
                            ) : null}
                          </Box>
                        ))}
                        {assignments.length > 2 ? (
                          <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', pt: 0.5 }}>
                            +{assignments.length - 2} more resource{assignments.length - 2 !== 1 ? 's' : ''}
                          </Typography>
                        ) : null}
                      </Stack>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {canEdit ? 'No assignments yet — click to add.' : 'No assignments yet.'}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              ) : null}


              {showStaffingBlock ? (
              <Grid item xs={12} md={6}>
                <Card
                  variant="outlined"
                  sx={sectionCardSx('#EF4444')}
                  onClick={() => openArtifact('staffing')}
                >
                  <CardContent>
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Box sx={{ width: 4, height: 20, bgcolor: '#EF4444', borderRadius: 1 }} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 650 }}>
                          Staffing Requests
                        </Typography>
                      </Stack>
                      <Chip
                        size="small"
                        label={staffingRequests.length}
                        sx={{
                          height: 24,
                          fontWeight: 650,
                          bgcolor: staffingRequests.length > 0 ? tokens.color.dangerSoft : 'transparent',
                          color: staffingRequests.length > 0 ? tokens.color.danger : 'inherit',
                        }}
                      />
                    </Stack>
                    {staffingRequests.length ? (
                      <Stack spacing={1}>
                        {staffingRequests.slice(0, 2).map((r) => (
                          <Box
                            key={r.id}
                            sx={{
                              p: 1,
                              bgcolor: tokens.color.surface,
                              borderRadius: 1,
                              border: `1px solid ${tokens.color.border}`,
                            }}
                          >
                            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                              <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                                {r.title}
                              </Typography>
                              <Chip
                                size="small"
                                label={titleCase(String(r.status || '—'))}
                                sx={{
                                  height: 20,
                                  fontSize: '0.7rem',
                                  bgcolor: r.status === 'APPROVED' ? tokens.color.successSoft : r.status === 'REJECTED' ? tokens.color.dangerSoft : tokens.color.warningSoft,
                                  color: r.status === 'APPROVED' ? tokens.color.success : r.status === 'REJECTED' ? tokens.color.danger : tokens.color.warning,
                                }}
                              />
                            </Stack>
                            {r.priority ? (
                              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                Priority: {titleCase(r.priority)}
                              </Typography>
                            ) : null}
                          </Box>
                        ))}
                        {staffingRequests.length > 2 ? (
                          <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', pt: 0.5 }}>
                            +{staffingRequests.length - 2} more request{staffingRequests.length - 2 !== 1 ? 's' : ''}
                          </Typography>
                        ) : null}
                      </Stack>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {canEdit ? 'No staffing requests yet — click to add one.' : 'No staffing requests.'}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              ) : null}

              {showMilestonesBlock ? (
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={sectionCardSx('#8B5CF6')} onClick={() => openArtifact('milestones')}>
                  <CardContent>
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 650 }}>
                        Milestones
                      </Typography>
                      <Chip size="small" variant="outlined" label={milestones.length} sx={{ height: 24, fontWeight: 650 }} />
                    </Stack>
                    {milestones.length ? (
                      <Stack spacing={0.5}>
                        {milestones.slice(0, 2).map((m: any) => (
                          <Typography key={m.id} variant="body2" color="text.secondary" noWrap>
                            - {m.name} ({titleCase(String(m.status || '—'))})
                          </Typography>
                        ))}
                        {milestones.length > 2 ? (
                          <Typography variant="caption" color="text.secondary">
                            +{milestones.length - 2} more
                          </Typography>
                        ) : null}
                      </Stack>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {canEdit ? 'No milestones yet — click to view.' : 'No milestones.'}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              ) : null}
            </Grid>

            {/* If this project didn't come from a template and has no artifacts yet, keep the summary clean. */}
            {!createdFromTemplateId && !showNotesBlock && !showFilesBlock && !showTeamBlock && !showResourcesBlock && !showStaffingBlock ? (
              <Box sx={{ mt: 2 }}>
                <Alert severity="info" variant="outlined">
                  No template artifacts on this project yet.
                </Alert>
              </Box>
            ) : null}
          </div>
        ) : null}

        {viewMode === 'board' ? (
          <div className="view-content board-view-content">
            <div className="kanban-board">
              {boardColumns.map((col) => (
                <div key={col} className="kanban-column">
                  <div className="kanban-column-header">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <h4>{columnLabel(col)}</h4>
                        <span className="column-count">({(filteredBoard[col] || []).length})</span>
                      </Box>
                      <Tooltip title="Add task">
                        <IconButton size="small" onClick={() => openCreateInColumn(col)}>
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </div>
                  <div
                    className={`kanban-column-content ${dragOverCol === col ? 'drag-over' : ''}`}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setDragOverCol(col);
                    }}
                    onDragLeave={() => {
                      setDragOverCol(null);
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      const droppedId = e.dataTransfer.getData('text/plain') || draggingId;
                      setDragOverCol(null);
                      setDraggingId(null);
                      if (droppedId) moveCard(droppedId, col);
                    }}
                  >
                    {(filteredBoard[col] || []).length > 0 ? (
                      (filteredBoard[col] || []).map((c) => (
                        <div
                          key={c.id}
                          className={`kanban-card ${draggingId === c.id ? 'dragging' : ''}`}
                          draggable
                          onDragStart={(e) => {
                            e.dataTransfer.setData('text/plain', c.id);
                            e.dataTransfer.effectAllowed = 'move';
                            setDraggingId(c.id);
                          }}
                          onDragEnd={() => setDraggingId(null)}
                          onClick={() => openCard(c)}
                        >
                          <div className="kanban-card-header">
                            <div className="issue-key-small">{c.id.slice(0, 8)}</div>
                            <span className="issue-status-badge" style={{ backgroundColor: '#3B82F6' }}>
                              {titleCase(String(c.status))}
                            </span>
                          </div>
                          <div className="kanban-card-title">{c.title}</div>
                          <Box sx={{ mt: 1, display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
                            {c.assigneeId ? (
                              <Chip
                                size="small"
                                label={
                                  meUser?.id && c.assigneeId === meUser.id
                                    ? 'Mine'
                                    : c.Assignee?.displayName || c.Assignee?.email || 'Claimed'
                                }
                                sx={{
                                  height: 22,
                                  fontWeight: 650,
                                  ...(meUser?.id && c.assigneeId === meUser.id
                                    ? { backgroundColor: tokens.color.successSoft, color: tokens.color.success }
                                    : { backgroundColor: tokens.color.neutralSoft, color: tokens.color.text }),
                                }}
                              />
                            ) : (
                              <Chip size="small" variant="outlined" label="Unclaimed" sx={{ height: 22, fontWeight: 700 }} />
                            )}
                            {c.dueDate ? (
                              <Chip size="small" variant="outlined" label={`Due ${String(c.dueDate).slice(0, 10)}`} sx={{ height: 22, fontWeight: 700 }} />
                            ) : null}
                            {(c.tags || []).slice(0, 3).map((t) => (
                              <Chip key={t} size="small" label={t} sx={{ height: 22 }} />
                            ))}
                          </Box>
                          {c.dueDate ? (
                            <div className="kanban-card-footer">
                              <div className="kanban-card-due-date">
                                <span style={{ fontWeight: 600, fontSize: '11px', color: '#F59E0B' }}>
                                  📅 {String(c.dueDate).slice(0, 10)}
                                </span>
                              </div>
                            </div>
                          ) : null}
                        </div>
                      ))
                    ) : (
                      <div className="empty-column">
                        <p>No tasks</p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {viewMode === 'list' ? (
          <Box sx={{ height: 620, backgroundColor: tokens.color.surface, border: `1px solid ${tokens.color.border}`, borderRadius: `${tokens.radius.md}px` }}>
            <ResizableDataGrid
              storageKey={`gridWidths.project.${id || 'unknown'}.tasks`}
              enableDensityControls
              rows={cards}
              columns={cols}
              getRowId={(r) => r.id}
              disableRowSelectionOnClick
              onRowClick={(params) => openCard(params.row)}
              pageSizeOptions={[10, 25, 50]}
              initialState={{ pagination: { paginationModel: { pageSize: 10, page: 0 } } }}
            />
          </Box>
        ) : null}

        {viewMode === 'timeline' ? (
          <div className="view-content timeline-view-content">
            <div className="timeline-container">
              {cards
                .filter((c) => !!c.dueDate)
                .slice()
                .sort((a, b) => new Date(a.dueDate as any).getTime() - new Date(b.dueDate as any).getTime())
                .map((c, index, arr) => (
                  <div key={c.id} className="timeline-item">
                    <div className="timeline-dot" />
                    <div className="timeline-card" onClick={() => openCard(c)} style={{ cursor: 'pointer' }}>
                      <div className="timeline-card-header">
                        <div className="timeline-card-title">{c.title}</div>
                      </div>
                      <div className="timeline-card-meta">
                        <span className="timeline-meta-item">🧩 {titleCase(String(c.status))}</span>
                      </div>
                      <div className="timeline-card-footer">
                        <div className="timeline-card-date">
                          📅 Due {String(c.dueDate).slice(0, 10)}
                        </div>
                      </div>
                    </div>
                    {index < arr.length - 1 && <div className="timeline-connector" />}
                  </div>
                ))}
            </div>
          </div>
        ) : null}

        {viewMode === 'finances' ? (
          <div className="view-content">
            {!canViewFinancials ? (
              <Alert severity="info" variant="outlined">
                You do not have access to financials.
              </Alert>
            ) : (
              <>
                {financialEntriesError ? (
                  <Alert severity="error" variant="filled" sx={{ mb: 2 }}>
                    {financialEntriesError}
                  </Alert>
                ) : null}

                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <StatCard
                      title="Budget"
                      value={formatCurrency(finance.budget)}
                      subtitle="Approved ceiling (BAC)"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <StatCard
                      title="Spent"
                      value={formatCurrency(finance.spent)}
                      subtitle={`${Math.round(finance.utilizationPct)}% of budget`}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <StatCard
                      title="ETC"
                      value={formatCurrency(finance.etc)}
                      subtitle="Estimate to complete"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <StatCard
                      title="EAC"
                      value={formatCurrency(finance.eac)}
                      subtitle="Forecast at completion"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <StatCard
                      title="Variance"
                      value={formatSignedCurrencyShort(finance.variance)}
                      subtitle={finance.varianceLabel}
                      tone={finance.tone}
                      accent={finance.tone === 'unfavorable' ? tokens.color.danger : finance.tone === 'favorable' ? tokens.color.success : undefined}
                    />
                  </Grid>
                </Grid>

                <Card variant="outlined" sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ fontWeight: 650, mb: 1 }}>
                      Budget vs Spend
                    </Typography>
                    <NexusBarChart
                      data={[
                        { name: 'Budget', value: finance.budget },
                        { name: 'Spent', value: finance.spent },
                        { name: 'EAC', value: finance.eac },
                      ]}
                      xKey="name"
                      series={[{ key: 'value', name: 'Amount', color: tokens.color.accent }]}
                      valueFormatter={formatCurrencyShort}
                      height={240}
                    />
                  </CardContent>
                </Card>

                <Box sx={{ height: 620, backgroundColor: tokens.color.surface, border: `1px solid ${tokens.color.border}`, borderRadius: `${tokens.radius.md}px` }}>
                  <ResizableDataGrid
                    storageKey={`gridWidths.project.${id || 'unknown'}.finances`}
                    enableDensityControls
                    rows={(financialEntries || []).filter((f: any) => {
                      const t = String(f?.type || '').toUpperCase();
                      return t !== 'FUNDING' && t !== 'ALLOCATION';
                    })}
                    columns={financialCols}
                    getRowId={(r: any) => r.id}
                    disableRowSelectionOnClick
                    loading={financialEntriesLoading}
                    pageSizeOptions={[10, 25, 50]}
                    initialState={{ pagination: { paginationModel: { pageSize: 10, page: 0 } } }}
                  />
                </Box>
              </>
            )}
          </div>
        ) : null}
      </div>

      <Dialog open={!!artifactDialog} onClose={() => setArtifactDialog(null)} fullWidth maxWidth="md">
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
            <span>
              {artifactDialog === 'notes'
                ? 'Notes'
                : artifactDialog === 'files'
                  ? 'Files'
                  : artifactDialog === 'team'
                    ? 'Team & Permissions'
                    : artifactDialog === 'resources'
                      ? 'Resources'
                      : artifactDialog === 'staffing'
                        ? 'Staffing Requests'
                        : 'Details'}
            </span>
            {artifactDialog === 'notes' && (canEdit || isProjectMember) && meUser?.id ? (
              <IconButton size="small" onClick={() => setAddNoteDialogOpen(true)} sx={{ ml: 'auto' }}>
                <AddIcon />
              </IconButton>
            ) : null}
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ maxHeight: '80vh', overflowY: 'auto' }}>
          {artifactDialog === 'notes' ? (
            <>

              {selectedNoteId ? (
                <Box sx={{ mb: 2 }}>
                  <Button startIcon={<EditIcon />} onClick={() => setSelectedNoteId(null)} sx={{ mb: 1 }}>
                    ← Back to notes
                  </Button>
                  {(() => {
                    const note = discussions.find((d) => d.id === selectedNoteId);
                    if (!note) return <Alert severity="error">Note not found</Alert>;
                    const firstPost = note.Posts?.[0];
                    const author = firstPost?.User?.displayName || firstPost?.User?.email || 'Unknown';
                    const date = firstPost?.createdAt ? new Date(firstPost.createdAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '';
                    return (
                      <Card variant="outlined" sx={{ p: 2 }}>
                        <Stack spacing={1.5}>
                          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                            <Stack direction="row" spacing={1} alignItems="center">
                              {note.isPinned ? <Chip size="small" label="Pinned" color="primary" /> : null}
                              <Typography variant="h6" sx={{ fontWeight: 650 }}>
                                {note.title}
                              </Typography>
                            </Stack>
                            {meUser?.id && note.creatorId === meUser.id ? (
                              <IconButton size="small" onClick={() => handleDeleteNote(note.id)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            ) : null}
                          </Stack>
                          <Stack direction="row" spacing={2}>
                            <Typography variant="body2" color="text.secondary">
                              By: {author}
                            </Typography>
                            {date ? (
                              <Typography variant="body2" color="text.secondary">
                                • {date}
                              </Typography>
                            ) : null}
                          </Stack>
                          <Divider />
                          <Box 
                            sx={{ 
                              '& .ql-editor': { 
                                padding: 0,
                                fontSize: '0.9375rem',
                                lineHeight: 1.7,
                              },
                              '& .ql-container': {
                                border: 'none',
                                fontFamily: 'inherit',
                              },
                              '& ol, & ul': {
                                paddingLeft: '1.5em',
                                marginLeft: 0,
                              },
                              '& li': {
                                marginBottom: '0.25em',
                              },
                              wordWrap: 'break-word',
                              overflowWrap: 'break-word',
                            }}
                          >
                            <div dangerouslySetInnerHTML={{ __html: firstPost?.content || '<p>No content</p>' }} />
                          </Box>
                          {note.Posts && note.Posts.length > 1 ? (
                            <>
                              <Divider sx={{ my: 2 }} />
                              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                                Additional posts ({note.Posts.length - 1})
                              </Typography>
                              <Box sx={{ maxHeight: 400, overflowY: 'auto', pr: 1 }}>
                                {note.Posts.slice(1).map((post, idx) => (
                                  <Box key={post.id} sx={{ pl: 2, borderLeft: '2px solid #E5E7EB', mb: 2 }}>
                                    <Stack direction="row" spacing={1} sx={{ mb: 0.5 }}>
                                      <Typography variant="caption" color="text.secondary">
                                        {post.User?.displayName || post.User?.email || 'Unknown'}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        • {new Date(post.createdAt).toLocaleDateString()}
                                      </Typography>
                                    </Stack>
                                    <Box 
                                      sx={{ 
                                        '& .ql-editor': { 
                                          padding: 0,
                                          fontSize: '0.875rem',
                                        },
                                        '& .ql-container': {
                                          border: 'none',
                                          fontFamily: 'inherit',
                                        },
                                        '& ol, & ul': {
                                          paddingLeft: '1.5em',
                                          marginLeft: 0,
                                        },
                                        '& li': {
                                          marginBottom: '0.25em',
                                        },
                                        wordWrap: 'break-word',
                                        overflowWrap: 'break-word',
                                      }}
                                    >
                                      <div dangerouslySetInnerHTML={{ __html: post.content || '<p></p>' }} />
                                    </Box>
                                  </Box>
                                ))}
                              </Box>
                            </>
                          ) : null}
                        </Stack>
                      </Card>
                    );
                  })()}
                </Box>
              ) : (
                <>
                  {discussions.length ? (
                    <>
                      <Grid container spacing={2}>
                        {discussions
                          .slice()
                          .sort((a, b) => (a.isPinned === b.isPinned ? 0 : a.isPinned ? -1 : 1))
                          .slice((notesPage - 1) * notesPerPage, notesPage * notesPerPage)
                          .map((d) => {
                            const firstPost = d.Posts?.[0];
                            const contentHtml = firstPost?.content || '';
                            const author = firstPost?.User?.displayName || firstPost?.User?.email || 'Unknown';
                            const date = firstPost?.createdAt ? new Date(firstPost.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
                            return (
                              <Grid item xs={12} sm={6} md={4} key={d.id}>
                                <Card
                                  variant="outlined"
                                  sx={{
                                    cursor: 'pointer',
                                    transition: 'border-color 120ms ease, background-color 120ms ease',
                                    '&:hover': { borderColor: tokens.color.borderStrong, backgroundColor: tokens.color.surfaceMuted },
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                  }}
                                  onClick={() => setSelectedNoteId(d.id)}
                                >
                                  <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                                    <Stack spacing={1} sx={{ flex: 1 }}>
                                      <Box sx={{ minHeight: 24 }}>
                                        {d.isPinned ? <Chip size="small" label="Pinned" color="primary" /> : null}
                                      </Box>
                                      <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                                        {d.title}
                                      </Typography>
                                      <Box sx={{ flex: 1, minHeight: 0 }}>
                                        {contentHtml ? (
                                          <Box
                                            sx={{
                                              position: 'relative',
                                              maxHeight: '4.5em',
                                              overflow: 'hidden',
                                              wordWrap: 'break-word',
                                              overflowWrap: 'break-word',
                                              '&::after': {
                                                content: '""',
                                                position: 'absolute',
                                                bottom: 0,
                                                left: 0,
                                                right: 0,
                                                height: '24px',
                                                background: 'linear-gradient(to bottom, rgba(255,255,255,0), rgba(255,255,255,1))',
                                                pointerEvents: 'none',
                                              },
                                              '& .ql-editor': {
                                                padding: 0,
                                                fontSize: '0.875rem',
                                                lineHeight: 1.5,
                                                color: 'text.secondary',
                                              },
                                              '& .ql-container': {
                                                border: 'none',
                                                fontFamily: 'inherit',
                                              },
                                              '& ol, & ul': {
                                                paddingLeft: '1.5em',
                                                marginLeft: 0,
                                                marginTop: '0.25em',
                                                marginBottom: '0.25em',
                                              },
                                              '& li': {
                                                marginBottom: '0.125em',
                                              },
                                              '& p': {
                                                marginTop: '0.25em',
                                                marginBottom: '0.25em',
                                                '&:first-of-type': {
                                                  marginTop: 0,
                                                },
                                                '&:last-of-type': {
                                                  marginBottom: 0,
                                                },
                                              },
                                            }}
                                          >
                                            <div dangerouslySetInnerHTML={{ __html: contentHtml }} />
                                          </Box>
                                        ) : null}
                                      </Box>
                                      <Stack direction="row" spacing={1} alignItems="center" sx={{ pt: 1 }}>
                                        <Typography variant="caption" color="text.secondary">
                                          {author}
                                        </Typography>
                                        {date ? (
                                          <>
                                            <Typography variant="caption" color="text.secondary">•</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                              {date}
                                            </Typography>
                                          </>
                                        ) : null}
                                      </Stack>
                                    </Stack>
                                  </CardContent>
                                </Card>
                              </Grid>
                            );
                          })}
                      </Grid>
                      {discussions.length > notesPerPage ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                          <Pagination
                            count={Math.ceil(discussions.length / notesPerPage)}
                            page={notesPage}
                            onChange={(_, value) => setNotesPage(value)}
                            color="primary"
                            size="large"
                          />
                        </Box>
                      ) : null}
                    </>
                  ) : (
                    <Alert severity="info">
                      {meUser?.id ? 'No notes yet. Click the + button to add one.' : 'No notes found.'}
                    </Alert>
                  )}
                </>
              )}
            </>
          ) : null}

          {artifactDialog === 'files' ? (
            <>
              {canEdit ? (
                <Box sx={{ mb: 2, p: 1.5, border: '1px solid #E5E7EB', borderRadius: 2 }}>
                  <Typography sx={{ fontWeight: 650, mb: 1 }}>Add file placeholder</Typography>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
                    <TextField
                      size="small"
                      label="Name"
                      value={fileForm.name}
                      onChange={(e) => setFileForm((f) => ({ ...f, name: e.target.value }))}
                      sx={{ flex: 1, minWidth: 240 }}
                    />
                    <TextField
                      size="small"
                      label="Type"
                      value={fileForm.type}
                      onChange={(e) => setFileForm((f) => ({ ...f, type: e.target.value }))}
                      sx={{ minWidth: 160 }}
                    />
                    <TextField
                      size="small"
                      label="URL (optional)"
                      value={fileForm.url}
                      onChange={(e) => setFileForm((f) => ({ ...f, url: e.target.value }))}
                      sx={{ flex: 1, minWidth: 240 }}
                    />
                    <Button variant="contained" disabled={fileSaving} onClick={addFilePlaceholder}>
                      Add
                    </Button>
                  </Stack>
                </Box>
              ) : null}

              {filePlaceholders.length ? (
                <List>
                  {filePlaceholders.map((f: any, idx: number) => (
                    <React.Fragment key={`${f?.name || 'file'}_${idx}`}>
                      <ListItem
                        secondaryAction={
                          canEdit ? (
                            <IconButton size="small" onClick={() => deleteFilePlaceholder(idx)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          ) : null
                        }
                      >
                        <ListItemText
                          primary={String(f?.name || 'Untitled')}
                          secondary={f?.url ? String(f.url) : (f?.type ? `Type: ${String(f.type)}` : '—')}
                        />
                      </ListItem>
                      {idx < filePlaceholders.length - 1 ? <Divider /> : null}
                    </React.Fragment>
                  ))}
                </List>
              ) : (
                <Alert severity="info">
                  No file placeholders found. (Templates store these under <code>Project.customFields.filePlaceholders</code>.)
                </Alert>
              )}
            </>
          ) : null}

          {artifactDialog === 'team' ? (
            <>
              {canEdit ? (
                <Box sx={{ mb: 2, p: 1.5, border: '1px solid #E5E7EB', borderRadius: 2 }}>
                  <Typography sx={{ fontWeight: 650, mb: 1 }}>Add member</Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                    <TextField
                      select
                      size="small"
                      label="User"
                      value={memberForm.userId}
                      onChange={(e) => setMemberForm((f) => ({ ...f, userId: e.target.value }))}
                      sx={{ minWidth: 260 }}
                    >
                      <MenuItem value="">
                        <em>Select user</em>
                      </MenuItem>
                      {allUsers.map((u) => (
                        <MenuItem key={u.id} value={u.id}>
                          {u.displayName || u.email}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      select
                      size="small"
                      label="Role"
                      value={memberForm.role}
                      onChange={(e) => setMemberForm((f) => ({ ...f, role: e.target.value }))}
                      sx={{ minWidth: 180 }}
                    >
                      <MenuItem value="member">member</MenuItem>
                      <MenuItem value="lead">lead</MenuItem>
                      <MenuItem value="viewer">viewer</MenuItem>
                    </TextField>
                    <Button variant="contained" disabled={!memberForm.userId || membersSaving} onClick={addMember}>
                      Add
                    </Button>
                  </Stack>
                </Box>
              ) : null}

              {members.length ? (
                <List>
                  {members.map((m, idx) => (
                    <React.Fragment key={m.id}>
                      <ListItem
                        secondaryAction={
                          canEdit ? (
                            <Stack direction="row" spacing={1} alignItems="center">
                              <TextField
                                select
                                size="small"
                                value={m.role || 'member'}
                                onChange={(e) => updateMemberRole(m.id, e.target.value)}
                                sx={{ minWidth: 140 }}
                              >
                                <MenuItem value="member">member</MenuItem>
                                <MenuItem value="lead">lead</MenuItem>
                                <MenuItem value="viewer">viewer</MenuItem>
                              </TextField>
                              <IconButton size="small" onClick={() => removeMember(m.id)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          ) : null
                        }
                      >
                        <ListItemText
                          primary={m.User?.displayName || m.User?.email || m.userId}
                          secondary={`Role: ${m.role || 'member'}`}
                        />
                      </ListItem>
                      {idx < members.length - 1 ? <Divider /> : null}
                    </React.Fragment>
                  ))}
                </List>
              ) : (
                <Alert severity="info">No project members found.</Alert>
              )}
            </>
          ) : null}

          {artifactDialog === 'resources' ? (
            <>
              {canEdit ? (
                <Box sx={{ mb: 2, p: 1.5, border: '1px solid #E5E7EB', borderRadius: 2 }}>
                  <Typography sx={{ fontWeight: 650, mb: 1 }}>Add resource</Typography>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
                    <TextField
                      select
                      size="small"
                      label="User"
                      value={resourceForm.userId}
                      onChange={(e) => setResourceForm((f) => ({ ...f, userId: e.target.value }))}
                      sx={{ minWidth: 240 }}
                    >
                      <MenuItem value="">
                        <em>Select user</em>
                      </MenuItem>
                      {allUsers.map((u) => (
                        <MenuItem key={u.id} value={u.id}>
                          {u.displayName || u.email}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      size="small"
                      label="Role"
                      value={resourceForm.role}
                      onChange={(e) => setResourceForm((f) => ({ ...f, role: e.target.value }))}
                      sx={{ minWidth: 160 }}
                    />
                    <TextField
                      size="small"
                      label="Hours"
                      value={resourceForm.hoursAllocated}
                      onChange={(e) => setResourceForm((f) => ({ ...f, hoursAllocated: e.target.value }))}
                      sx={{ minWidth: 120 }}
                    />
                    <TextField
                      size="small"
                      type="date"
                      label="Start"
                      value={resourceForm.startDate}
                      onChange={(e) => setResourceForm((f) => ({ ...f, startDate: e.target.value }))}
                      sx={{ minWidth: 160 }}
                      InputLabelProps={{ shrink: true }}
                    />
                    <Button variant="contained" disabled={resourceSaving} onClick={addResource}>
                      Add
                    </Button>
                  </Stack>
                  <TextField
                    size="small"
                    label="Notes (optional)"
                    value={resourceForm.notes}
                    onChange={(e) => setResourceForm((f) => ({ ...f, notes: e.target.value }))}
                    fullWidth
                    sx={{ mt: 1 }}
                  />
                </Box>
              ) : null}

              {assignments.length ? (
                <List>
                  {assignments.map((a, idx) => {
                    const cp = capacityByUserId[a.userId] || null;
                    const util = cp && cp.totalCapacity > 0 ? Math.round((cp.allocated / cp.totalCapacity) * 100) : null;
                    return (
                      <React.Fragment key={a.id}>
                        <ListItem
                          alignItems="flex-start"
                          secondaryAction={
                            canEdit ? (
                              <IconButton size="small" onClick={() => removeResource(a.id)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            ) : null
                          }
                        >
                          <ListItemText
                            primary={
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Typography sx={{ fontWeight: 650 }}>
                                  {a.User?.displayName || a.User?.email || a.userId}
                                </Typography>
                                {a.role ? <Chip size="small" label={a.role} /> : null}
                                {a.status ? <Chip size="small" variant="outlined" label={titleCase(String(a.status))} /> : null}
                              </Stack>
                            }
                            secondary={
                              <>
                                <div>Allocated: {a.hoursAllocated ?? 0}h</div>
                                {cp ? (
                                  <div>
                                    Capacity ({toPeriod(new Date())}): {cp.allocated}/{cp.totalCapacity}h ({util}%)
                                  </div>
                                ) : (
                                  <div>Capacity: —</div>
                                )}
                                {a.notes ? <div>Notes: {a.notes}</div> : null}
                              </>
                            }
                          />
                        </ListItem>
                        {idx < assignments.length - 1 ? <Divider /> : null}
                      </React.Fragment>
                    );
                  })}
                </List>
              ) : (
                <Alert severity="info">No resource assignments found.</Alert>
              )}
            </>
          ) : null}

          {artifactDialog === 'staffing' ? (
            <>
              {canEdit ? (
                <Box sx={{ mb: 2, p: 2, border: `1px solid ${tokens.color.border}`, borderRadius: 2, bgcolor: tokens.color.surfaceMuted }}>
                  <Typography sx={{ fontWeight: 650, mb: 1.5, fontSize: 16 }}>Add staffing request</Typography>
                  <Stack spacing={1.5}>
                    <TextField
                      size="small"
                      label="Title"
                      value={staffingForm.title}
                      onChange={(e) => setStaffingForm((f) => ({ ...f, title: e.target.value }))}
                      fullWidth
                      placeholder="e.g., Need Senior Developer"
                    />
                    <TextField
                      size="small"
                      label="Description (optional)"
                      value={staffingForm.description}
                      onChange={(e) => setStaffingForm((f) => ({ ...f, description: e.target.value }))}
                      fullWidth
                      multiline
                      minRows={3}
                      placeholder="Describe the staffing need..."
                    />
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
                      <TextField
                        size="small"
                        select
                        label="Priority"
                        value={staffingForm.priority}
                        onChange={(e) => setStaffingForm((f) => ({ ...f, priority: e.target.value }))}
                        sx={{ minWidth: 180 }}
                      >
                        {['LOW', 'MEDIUM', 'HIGH', 'URGENT'].map((p) => (
                          <MenuItem key={p} value={p}>
                            {titleCase(p)}
                          </MenuItem>
                        ))}
                      </TextField>
                      <Button
                        variant="contained"
                        onClick={addStaffingRequest}
                        disabled={staffingSaving || !staffingForm.title.trim()}
                        sx={{ whiteSpace: 'nowrap' }}
                      >
                        {staffingSaving ? 'Adding…' : 'Add request'}
                      </Button>
                    </Stack>
                    {staffingSaving ? <LinearProgress /> : null}
                  </Stack>
                </Box>
              ) : null}

              {staffingRequests.length ? (
                <Grid container spacing={2}>
                  {staffingRequests.map((r) => (
                    <Grid item xs={12} sm={6} key={r.id}>
                      <Card
                        variant="outlined"
                        sx={{
                          border: `1px solid ${tokens.color.border}`,
                          bgcolor: tokens.color.surface,
                          transition: 'border-color 120ms ease',
                          '&:hover': { borderColor: tokens.color.borderStrong },
                        }}
                      >
                        <CardContent>
                          <Stack spacing={1}>
                            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                              <Typography variant="subtitle1" sx={{ fontWeight: 650 }}>
                                {r.title}
                              </Typography>
                              {canEdit ? (
                                <IconButton size="small" onClick={() => deleteStaffingRequest(r.id)}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              ) : null}
                            </Stack>
                            <Stack direction="row" spacing={1} flexWrap="wrap">
                              <Chip
                                size="small"
                                label={titleCase(String(r.status))}
                                sx={{
                                  bgcolor: r.status === 'APPROVED' ? tokens.color.successSoft : r.status === 'REJECTED' ? tokens.color.dangerSoft : tokens.color.warningSoft,
                                  color: r.status === 'APPROVED' ? tokens.color.success : r.status === 'REJECTED' ? tokens.color.danger : tokens.color.warning,
                                  fontWeight: 600,
                                }}
                              />
                              {r.priority ? (
                                <Chip
                                  size="small"
                                  label={titleCase(String(r.priority))}
                                  sx={{
                                    bgcolor: r.priority === 'URGENT' ? tokens.color.dangerSoft : r.priority === 'HIGH' ? tokens.color.warningSoft : tokens.color.accentSoft,
                                    color: r.priority === 'URGENT' ? tokens.color.danger : r.priority === 'HIGH' ? tokens.color.warning : tokens.color.accent,
                                    fontWeight: 600,
                                  }}
                                />
                              ) : null}
                            </Stack>
                            {r.description ? (
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                {String(r.description).slice(0, 200)}{r.description.length > 200 ? '...' : ''}
                              </Typography>
                            ) : null}
                          </Stack>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Alert severity="info">
                  {canEdit ? 'No staffing requests yet. Add one above to get started.' : 'No staffing requests found.'}
                </Alert>
              )}
            </>
          ) : null}

          {artifactDialog === 'milestones' ? (
            <>
              {canEdit ? (
                <Box sx={{ mb: 2, p: 1.5, border: '1px solid #E5E7EB', borderRadius: 2 }}>
                  <Typography sx={{ fontWeight: 650, mb: 1 }}>{editingMilestone ? 'Edit milestone' : 'Add milestone'}</Typography>
                  <Stack spacing={1}>
                    <TextField
                      size="small"
                      label="Name"
                      value={milestoneForm.name}
                      onChange={(e) => setMilestoneForm((f) => ({ ...f, name: e.target.value }))}
                      fullWidth
                    />
                    <TextField
                      size="small"
                      label="Description (optional)"
                      value={milestoneForm.description}
                      onChange={(e) => setMilestoneForm((f) => ({ ...f, description: e.target.value }))}
                      fullWidth
                      multiline
                      minRows={2}
                    />
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                      <TextField
                        size="small"
                        select
                        label="Status"
                        value={milestoneForm.status}
                        onChange={(e) => setMilestoneForm((f) => ({ ...f, status: e.target.value }))}
                        sx={{ minWidth: 180 }}
                      >
                        {['NOT_STARTED', 'IN_PROGRESS', 'COMPLETED', 'DELAYED', 'AT_RISK'].map((s) => (
                          <MenuItem key={s} value={s}>
                            {titleCase(s.replace('_', ' '))}
                          </MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        size="small"
                        type="date"
                        label="Planned date"
                        value={milestoneForm.plannedDate}
                        onChange={(e) => setMilestoneForm((f) => ({ ...f, plannedDate: e.target.value }))}
                        sx={{ minWidth: 180 }}
                        InputLabelProps={{ shrink: true }}
                      />
                      <TextField
                        size="small"
                        label="Progress %"
                        value={milestoneForm.percentComplete}
                        onChange={(e) => setMilestoneForm((f) => ({ ...f, percentComplete: e.target.value }))}
                        sx={{ width: 120 }}
                        type="number"
                        inputProps={{ min: 0, max: 100 }}
                      />
                    </Stack>
                    <Stack direction="row" spacing={1}>
                      <Button variant="contained" onClick={editingMilestone ? () => updateMilestone(editingMilestone.id) : addMilestone} disabled={milestoneSaving || !milestoneForm.name.trim()}>
                        {milestoneSaving ? 'Saving…' : editingMilestone ? 'Update' : 'Add'}
                      </Button>
                      {editingMilestone ? (
                        <Button variant="outlined" onClick={() => { setEditingMilestone(null); setMilestoneForm({ name: '', description: '', status: 'NOT_STARTED', plannedDate: new Date().toISOString().slice(0, 10), percentComplete: '0' }); }}>
                          Cancel
                        </Button>
                      ) : null}
                    </Stack>
                    {milestoneSaving ? <LinearProgress /> : null}
                  </Stack>
                </Box>
              ) : null}

              {milestones.length ? (
                (() => {
                  const sortedMilestones = [...milestones].sort((a, b) => {
                    const aDate = a.plannedDate ? new Date(a.plannedDate).getTime() : 0;
                    const bDate = b.plannedDate ? new Date(b.plannedDate).getTime() : 0;
                    return aDate - bDate;
                  });
                  const now = new Date();
                  const earliest = sortedMilestones[0]?.plannedDate ? new Date(sortedMilestones[0].plannedDate) : now;
                  const latest = sortedMilestones[sortedMilestones.length - 1]?.plannedDate ? new Date(sortedMilestones[sortedMilestones.length - 1].plannedDate) : addMonths(now, 6);
                  const rangeStart = startOfMonth(earliest);
                  const rangeEnd = addMonths(startOfMonth(latest), 1);
                  const months: Array<{ label: string; start: Date; end: Date }> = [];
                  for (let d = rangeStart; d.getTime() < rangeEnd.getTime(); d = addMonths(d, 1)) {
                    months.push({ label: format(d, 'MMM yyyy'), start: d, end: addMonths(d, 1) });
                  }
                  const msStart = rangeStart.getTime();
                  const msEnd = rangeEnd.getTime();
                  const span = Math.max(1, msEnd - msStart);
                  const segWidth = 180;
                  const totalWidth = months.length * segWidth;
                  
                  return (
                    <Box sx={{ overflowX: 'auto', pb: 2 }}>
                      <Box sx={{ minWidth: totalWidth, position: 'relative', height: 200 }}>
                        {/* Month headers */}
                        <Box sx={{ display: 'flex', borderBottom: '2px solid #E5E7EB', mb: 2 }}>
                          {months.map((m) => (
                            <Box key={m.label} sx={{ width: segWidth, textAlign: 'center', py: 1, borderRight: '1px solid #E5E7EB' }}>
                              <Typography variant="caption" sx={{ fontWeight: 700 }}>
                                {m.label}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                        {/* Timeline line */}
                        <Box sx={{ position: 'absolute', top: 60, left: 0, right: 0, height: 2, bgcolor: tokens.color.border, zIndex: 0 }} />
                        {/* Current date indicator */}
                        {isWithinInterval(now, { start: rangeStart, end: rangeEnd }) ? (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 58,
                              left: `${((now.getTime() - msStart) / span) * 100}%`,
                              width: 4,
                              height: 6,
                              bgcolor: '#EF4444',
                              borderRadius: 1,
                              zIndex: 2,
                            }}
                          />
                        ) : null}
                        {/* Milestones */}
                        {sortedMilestones.map((m: any) => {
                          const plannedDate = m.plannedDate ? new Date(m.plannedDate) : null;
                          if (!plannedDate) return null;
                          const leftPct = ((plannedDate.getTime() - msStart) / span) * 100;
                          const statusColors: Record<string, string> = {
                            COMPLETED: '#10B981',
                            IN_PROGRESS: '#3B82F6',
                            DELAYED: '#EF4444',
                            AT_RISK: '#F59E0B',
                            NOT_STARTED: '#6B7280',
                          };
                          const color = statusColors[m.status] || '#6B7280';
                          return (
                            <Box
                              key={m.id}
                              sx={{
                                position: 'absolute',
                                top: 50,
                                left: `${Math.max(0, Math.min(100, leftPct))}%`,
                                transform: 'translateX(-50%)',
                                zIndex: 1,
                              }}
                            >
                              <Tooltip
                                title={
                                  <Stack spacing={0.5}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                      {m.name}
                                    </Typography>
                                    {m.description ? <Typography variant="body2">{m.description}</Typography> : null}
                                    <Typography variant="caption">
                                      Planned: {format(plannedDate, 'MMM d, yyyy')}
                                    </Typography>
                                    {m.actualDate ? (
                                      <Typography variant="caption">
                                        Actual: {format(new Date(m.actualDate), 'MMM d, yyyy')}
                                      </Typography>
                                    ) : null}
                                    {m.percentComplete != null ? (
                                      <Typography variant="caption">Progress: {Math.round(m.percentComplete)}%</Typography>
                                    ) : null}
                                  </Stack>
                                }
                              >
                                <Box
                                  sx={{
                                    width: 16,
                                    height: 16,
                                    borderRadius: '50%',
                                    bgcolor: color,
                                    border: `2px solid ${tokens.color.surface}`,
                                    cursor: 'pointer',
                                    '&:hover': { transform: 'scale(1.3)' },
                                    transition: 'transform 0.2s',
                                  }}
                                  onClick={() => {
                                    if (canEdit) {
                                      setEditingMilestone(m);
                                      setMilestoneForm({
                                        name: m.name || '',
                                        description: m.description || '',
                                        status: m.status || 'NOT_STARTED',
                                        plannedDate: m.plannedDate ? new Date(m.plannedDate).toISOString().slice(0, 10) : new Date().toISOString().slice(0, 10),
                                        percentComplete: String(m.percentComplete ?? 0),
                                      });
                                    }
                                  }}
                                />
                              </Tooltip>
                              <Box
                                sx={{
                                  position: 'absolute',
                                  top: 20,
                                  left: '50%',
                                  transform: 'translateX(-50%)',
                                  whiteSpace: 'nowrap',
                                  bgcolor: tokens.color.surface,
                                  px: 1,
                                  py: 0.5,
                                  borderRadius: 1,
                                  border: `1px solid ${color}`,
                                  maxWidth: 150,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }}
                              >
                                <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                                  {m.name}
                                </Typography>
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                      {/* Legend */}
                      <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        {sortedMilestones.map((m: any) => (
                          <Card
                            key={m.id}
                            variant="outlined"
                            sx={{
                              p: 1.5,
                              flex: '1 1 300px',
                              maxWidth: 400,
                              cursor: canEdit ? 'pointer' : 'default',
                              '&:hover': canEdit ? { borderColor: tokens.color.borderStrong } : {},
                            }}
                            onClick={() => {
                              if (canEdit) {
                                setEditingMilestone(m);
                                setMilestoneForm({
                                  name: m.name || '',
                                  description: m.description || '',
                                  status: m.status || 'NOT_STARTED',
                                  plannedDate: m.plannedDate ? new Date(m.plannedDate).toISOString().slice(0, 10) : new Date().toISOString().slice(0, 10),
                                  percentComplete: String(m.percentComplete ?? 0),
                                });
                              }
                            }}
                          >
                            <Stack spacing={1}>
                              <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                  {m.name}
                                </Typography>
                                {canEdit ? (
                                  <Stack direction="row" spacing={0.5}>
                                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); setEditingMilestone(m); setMilestoneForm({ name: m.name || '', description: m.description || '', status: m.status || 'NOT_STARTED', plannedDate: m.plannedDate ? new Date(m.plannedDate).toISOString().slice(0, 10) : new Date().toISOString().slice(0, 10), percentComplete: String(m.percentComplete ?? 0) }); }}>
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); deleteMilestone(m.id); }}>
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Stack>
                                ) : null}
                              </Stack>
                              <Stack direction="row" spacing={1} flexWrap="wrap">
                                <Chip size="small" label={titleCase(String(m.status || 'NOT_STARTED').replace('_', ' '))} />
                                {m.percentComplete != null ? (
                                  <Chip size="small" label={`${Math.round(m.percentComplete)}%`} />
                                ) : null}
                              </Stack>
                              {m.description ? (
                                <Typography variant="body2" color="text.secondary">
                                  {m.description}
                                </Typography>
                              ) : null}
                              <Stack direction="row" spacing={2}>
                                <Typography variant="caption" color="text.secondary">
                                  Planned: {m.plannedDate ? format(new Date(m.plannedDate), 'MMM d, yyyy') : '—'}
                                </Typography>
                                {m.actualDate ? (
                                  <Typography variant="caption" color="text.secondary">
                                    Actual: {format(new Date(m.actualDate), 'MMM d, yyyy')}
                                  </Typography>
                                ) : null}
                              </Stack>
                            </Stack>
                          </Card>
                        ))}
                      </Box>
                    </Box>
                  );
                })()
              ) : (
                <Alert severity="info">
                  {canEdit ? 'No milestones yet. Add one above or use a template that includes milestones.' : 'No milestones found.'}
                </Alert>
              )}
            </>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setArtifactDialog(null)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editProjectOpen} onClose={() => setEditProjectOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Edit project</DialogTitle>
        <DialogContent>
          <Stack spacing={1.25} sx={{ mt: 0.5 }}>
            <TextField
              label="Name"
              value={projectForm.name}
              onChange={(e) => setProjectForm((f) => ({ ...f, name: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Description"
              value={projectForm.description}
              onChange={(e) => setProjectForm((f) => ({ ...f, description: e.target.value }))}
              fullWidth
              multiline
              minRows={3}
            />
            <Stack direction="row" spacing={1}>
              <TextField
                select
                label="Status"
                value={projectForm.status}
                onChange={(e) => setProjectForm((f) => ({ ...f, status: e.target.value }))}
                fullWidth
              >
                {['PLANNING', 'ACTIVE', 'ON_TRACK', 'AT_RISK', 'OFF_TRACK', 'COMPLETED', 'CANCELLED', 'ARCHIVED'].map((s) => (
                  <MenuItem key={s} value={s}>
                    {titleCase(s)}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Priority"
                value={projectForm.priority}
                onChange={(e) => setProjectForm((f) => ({ ...f, priority: e.target.value }))}
                fullWidth
              >
                {['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'].map((p) => (
                  <MenuItem key={p} value={p}>
                    {titleCase(p)}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>

            <Box sx={{ mt: 0.5, p: 1.5, border: '1px solid #E5E7EB', borderRadius: 2 }}>
              <Typography sx={{ fontWeight: 700, mb: 1 }}>Budgeting</Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <TextField
                  label="Allocated Budget (project)"
                  value={projectForm.budgetedCost}
                  onChange={(e) => setProjectForm((f) => ({ ...f, budgetedCost: e.target.value }))}
                  fullWidth
                />
                <TextField
                  label="Actual Cost (derived)"
                  value={project?.actualCost != null ? String(project.actualCost) : ''}
                  fullWidth
                  disabled
                  helperText="Actual cost comes strictly from Financials entries (COST/EXPENSE). Use the Finances tab to add/update costs."
                />
              </Stack>
            </Box>

            <Box sx={{ mt: 0.75, p: 1.5, border: '1px solid #E5E7EB', borderRadius: 2 }}>
              <Typography sx={{ fontWeight: 700, mb: 1 }}>Ownership + Dates</Typography>
              <Stack spacing={1}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <TextField
                    select
                    label="Lead"
                    value={projectForm.leadUserId}
                    onChange={(e) => setProjectForm((f) => ({ ...f, leadUserId: e.target.value }))}
                    fullWidth
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {allUsers.map((u) => (
                      <MenuItem key={u.id} value={u.id}>
                        {u.displayName || u.username || u.email || u.id}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    label="Manager"
                    value={projectForm.managerUserId}
                    onChange={(e) => setProjectForm((f) => ({ ...f, managerUserId: e.target.value }))}
                    fullWidth
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {allUsers.map((u) => (
                      <MenuItem key={u.id} value={u.id}>
                        {u.displayName || u.username || u.email || u.id}
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <TextField
                    label="Start date"
                    type="date"
                    value={projectForm.startDate}
                    onChange={(e) => setProjectForm((f) => ({ ...f, startDate: e.target.value }))}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    label="Planned end date"
                    type="date"
                    value={projectForm.plannedEndDate}
                    onChange={(e) => setProjectForm((f) => ({ ...f, plannedEndDate: e.target.value }))}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Stack>
              </Stack>
            </Box>

            <Box sx={{ mt: 1, p: 1.5, border: '1px solid #E5E7EB', borderRadius: 2 }}>
              <Typography sx={{ fontWeight: 700, mb: 1 }}>Show sections</Typography>
              <Grid container spacing={1}>
                {[
                  { k: 'notes', label: 'Notes' },
                  { k: 'files', label: 'Files' },
                  { k: 'team', label: 'Team & Permissions' },
                  { k: 'resources', label: 'Resources' },
                  { k: 'subprojects', label: 'Subprojects' },
                  { k: 'staffing', label: 'Staffing' },
                  { k: 'milestones', label: 'Milestones' },
                ].map((x) => (
                  <Grid item xs={6} key={x.k}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={(projectSectionsForm as any)[x.k]}
                          onChange={(e) => setProjectSectionsForm((s) => ({ ...s, [x.k]: e.target.checked } as any))}
                        />
                      }
                      label={x.label}
                    />
                  </Grid>
                ))}
              </Grid>
              <Typography variant="caption" color="text.secondary">
                These toggles let you show sections even if the template didn't include them (or this project wasn't created from a template).
                <br />
                <strong>Note:</strong> "Team & Permissions" controls visibility. To actually add/remove team members, use the "Team & Permissions" section in the project summary view (click the folder icon or the Team card).
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setEditProjectOpen(false)}>
            Cancel
          </Button>
          <Button variant="contained" disabled={!canEdit || projectSaving} onClick={saveProject}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Task</DialogTitle>
        <DialogContent>
          {selected ? (
            <>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                <Chip size="small" label={titleCase(String(selected.status))} variant="outlined" sx={{ height: 22, fontWeight: 700 }} />
                <Chip size="small" label={selected.columnId || '—'} variant="outlined" sx={{ height: 22, fontWeight: 700 }} />
                <Chip size="small" label={selected.dueDate ? `Due ${String(selected.dueDate).slice(0, 10)}` : 'No due date'} variant="outlined" sx={{ height: 22, fontWeight: 700 }} />
                <Chip
                  size="small"
                  label={
                    selected.assigneeId
                      ? (meUser?.id && selected.assigneeId === meUser.id
                          ? 'Owned by me'
                          : `Owned by ${selected.Assignee?.displayName || selected.Assignee?.email || String(selected.assigneeId).slice(0, 8)}`)
                      : 'Unclaimed'
                  }
                  variant={selected.assigneeId ? 'filled' : 'outlined'}
                  sx={{
                    height: 22,
                    fontWeight: 650,
                    ...(selected.assigneeId && meUser?.id && selected.assigneeId === meUser.id
                      ? { backgroundColor: tokens.color.successSoft, color: tokens.color.success }
                      : {}),
                  }}
                />
              </Box>
              <Typography variant="h6" sx={{ mt: 1.25, fontWeight: 650 }}>
                {selected.title}
              </Typography>
              {selected.description ? (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {selected.description}
                </Typography>
              ) : null}
              {selected.tags?.length ? (
                <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {selected.tags.map((t) => (
                    <Chip key={t} size="small" label={t} />
                  ))}
                </Box>
              ) : null}
            </>
          ) : null}
        </DialogContent>
        <DialogActions>
          {selected && meUser?.id ? (
            selected.assigneeId ? (
              selected.assigneeId === meUser.id ? (
                <Button variant="outlined" onClick={() => unclaimCard(selected)}>
                  Unclaim
                </Button>
              ) : (
                <Button variant="outlined" disabled>
                  Claimed
                </Button>
              )
            ) : (
              <Button variant="contained" onClick={() => claimCard(selected)}>
                Claim
              </Button>
            )
          ) : null}
          {canEdit && selected ? (
            <>
              <Button variant="outlined" onClick={() => openEdit(selected)}>
                Edit
              </Button>
              <Button color="error" variant="outlined" onClick={() => remove(selected)}>
                Delete
              </Button>
            </>
          ) : null}
          <Button variant="contained" onClick={() => setDetailsOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editing ? 'Edit Task' : 'New Task'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mt: 1 }}>
          <TextField label="Title" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} fullWidth />
          <TextField label="Description" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} fullWidth multiline minRows={3} />
          <TextField
            label="Status"
            value={form.status}
            onChange={(e) => {
              const v = canonicalColumnKey(e.target.value);
              setForm((f) => ({ ...f, status: v, columnId: v }));
            }}
            select
            fullWidth
          >
            {['BACKLOG', 'TODO', 'IN_PROGRESS', 'IN_REVIEW', 'DONE', 'ARCHIVED'].map((s) => (
              <MenuItem key={s} value={s}>
                {titleCase(s)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Column"
            value={form.columnId}
            onChange={(e) => {
              const v = canonicalColumnKey(e.target.value);
              setForm((f) => ({ ...f, columnId: v, status: v }));
            }}
            select
            fullWidth
          >
            {boardColumns.map((c) => (
              <MenuItem key={c} value={c}>
                {columnLabel(c)}
              </MenuItem>
            ))}
          </TextField>
          <TextField label="Due Date" type="date" value={form.dueDate} onChange={(e) => setForm((f) => ({ ...f, dueDate: e.target.value }))} fullWidth InputLabelProps={{ shrink: true }} />
          <TextField label="Tags (comma separated)" value={form.tags} onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))} fullWidth />
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setEditOpen(false)}>
            Cancel
          </Button>
          <Button variant="contained" onClick={save}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Note Dialog */}
      <Dialog open={addNoteDialogOpen} onClose={() => { setAddNoteDialogOpen(false); setNoteForm({ title: '', content: '', pinned: false }); setNotesPage(1); }} fullWidth maxWidth="md">
        <DialogTitle>Add note</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
              size="small"
              label="Pinned"
              value={noteForm.pinned ? 'yes' : 'no'}
              onChange={(e) => setNoteForm((f) => ({ ...f, pinned: e.target.value === 'yes' }))}
              sx={{ minWidth: 140 }}
            >
              <MenuItem value="yes">Yes</MenuItem>
              <MenuItem value="no">No</MenuItem>
            </TextField>
            <TextField
              size="small"
              label="Title"
              value={noteForm.title}
              onChange={(e) => setNoteForm((f) => ({ ...f, title: e.target.value }))}
              fullWidth
              placeholder="e.g., 1/11 Meeting Notes"
            />
            <Box sx={{ width: '100%' }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                Content
              </Typography>
              <Box sx={{ 
                '& .ql-container': {
                  borderRadius: '4px',
                  border: '1px solid rgba(0, 0, 0, 0.23)',
                  fontSize: '0.875rem',
                },
                '& .ql-toolbar': {
                  borderRadius: '4px 4px 0 0',
                  border: '1px solid rgba(0, 0, 0, 0.23)',
                  borderBottom: 'none',
                },
                '& .ql-editor': {
                  minHeight: '200px',
                  maxHeight: '400px',
                  overflowY: 'auto',
                },
              }}>
                <ReactQuill
                  theme="snow"
                  value={noteForm.content}
                  onChange={(value) => setNoteForm((f) => ({ ...f, content: value }))}
                  placeholder="Enter note content..."
                  modules={{
                    toolbar: [
                      [{ 'header': [1, 2, 3, false] }],
                      ['bold', 'italic', 'underline', 'strike'],
                      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                      [{ 'size': ['small', false, 'large', 'huge'] }],
                      [{ 'color': [] }, { 'background': [] }],
                      ['link'],
                      ['clean']
                    ],
                  }}
                />
              </Box>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setAddNoteDialogOpen(false); setNoteForm({ title: '', content: '', pinned: false }); }}>
            Cancel
          </Button>
          <Button variant="contained" disabled={noteSaving || !noteForm.title.trim() || !noteForm.content.trim() || noteForm.content === '<p><br></p>'} onClick={createNote}>
            {noteSaving ? 'Adding...' : 'Add note'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Note Confirmation Dialog */}
      <Dialog open={deleteNoteConfirmOpen} onClose={() => { setDeleteNoteConfirmOpen(false); setNoteToDelete(null); }}>
        <DialogTitle>Delete note?</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this note? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDeleteNoteConfirmOpen(false); setNoteToDelete(null); }}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={confirmDeleteNote}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={toastOpen} autoHideDuration={2500} onClose={() => setToastOpen(false)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity={toast.severity} variant="filled">
          {toast.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default InternalProjectDetail;


